"use client"

import type React from "react"
import { useState, useRef, useEffect, useCallback } from "react"
import { Button } from "@edemics/ui/button"
import { Tabs, TabsList, TabsTrigger } from "@edemics/ui/tabs"
import { Input } from "@edemics/ui/input"
import { Avatar, AvatarFallback, AvatarImage } from "@edemics/ui/avatar"
import { ScrollArea } from "@edemics/ui/scroll-area"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@edemics/ui/dialog"
import { Label } from "@edemics/ui/label"
import { Textarea } from "@edemics/ui/textarea"
import { Checkbox } from "@edemics/ui/checkbox"
import { Switch } from "@edemics/ui/switch"
import { Slider } from "@edemics/ui/slider"
import { Separator } from "@edemics/ui/separator"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@edemics/ui/tooltip"
import {
  User,
  Users,
  X,
  Search,
  Paperclip,
  Mic,
  Smile,
  Send,
  ThumbsUp,
  MessageCircle,
  ListChecks,
  CalendarPlus,
  UsersRound,
  Settings2,
  FileText,
  FileImage,
  FileAudio,
  FileVideo,
  FileArchive,
  FileQuestion,
  Trash2,
  MoreVertical,
  Reply,
  Copy,
  Forward,
  CheckSquare,
  Pin,
  UserPlus,
  Settings,
  BellOff,
  LogOut,
  Info,
  Star,
  Check,
  CheckCheck,
  Download,
  Play,
  Pause,
  Edit3,
  Volume2,
  Heart,
  Calendar,
  Clock,
  Hash,
  Link2,
  AlertTriangle,
  PinOff,
  Bell,
  Image as ImageIcon,
  Video,
  Upload,
  Shield,
  Repeat,
  Plus,
  LifeBuoy,
} from "lucide-react"
// migrated from next/image → plain <img>. Audit responsive/optimization later.
import { Badge } from "@edemics/ui/badge"
import { cn } from "@edemics/ui/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@edemics/ui/dropdown-menu"
import { Popover, PopoverContent, PopoverTrigger } from "@edemics/ui/popover"
import { StandardInputField } from "./standard-input-field"
import { MultiSelectLinkedField } from "./multi-select-linked-field"
import { useChat } from "@edemics/contexts/chat-context"
// V5 §1.11 #5 — Chat Pop is BE-wired end-to-end:
//   • /chat/conversations hydrates the Direct/Group tabs
//   • /chat/conversations/:id/messages hydrates the open thread
//   • POST/PATCH/DELETE on /chat/{messages,reactions,...} drive the
//     writes, with chatSocket echoing live updates for every open
//     popup. All former module-level mock arrays have been removed —
//     empty state is now the honest signal when a tenant has no chats.
import {
  listConversations,
  listMessages as beListMessages,
  sendMessage as beSendMessage,
  editMessage as beEditMessage,
  deleteMessage as beDeleteMessage,
  addReaction as beAddReaction,
  removeReaction as beRemoveReaction,
  markRead as beMarkRead,
  markChatMessageUrgent,
  acknowledgeChatMessageUrgent,
  type ChatMessage as BEChatMessage,
} from "@edemics/services/communication/chatService"
import {
  connectChatSocket,
  disconnectChatSocket,
  subscribeToConversation,
  unsubscribeFromConversation,
  onChatEvent,
} from "@edemics/services/communication/chatSocket"

/**
 * Mirror of the private helper in /communication/chat/page.tsx — reads
 * the JWT the user signed in with so chatSocket.io can authenticate.
 * Prefers sessionStorage (session-handoff drain policy) over the
 * cookie, then falls back to localStorage.
 */
function readAuthToken(): string | null {
  if (typeof document === "undefined") return null
  const m = document.cookie.match(/(?:^|;\s*)(?:token|auth_token|access_token)=([^;]+)/)
  if (m) return decodeURIComponent(m[1])
  try {
    return (
      sessionStorage.getItem("token") ||
      localStorage.getItem("auth_token") ||
      localStorage.getItem("access_token")
    )
  } catch {
    return null
  }
}

// --- Helper Functions ---
const formatFileSize = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
}

const getFileIcon = (fileName: string) => {
  const extension = fileName.split(".").pop()?.toLowerCase()
  if (!extension) return <FileQuestion className="h-8 w-8 text-light-text-secondary dark:text-dark-text-secondary" />

  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension)) {
    return <FileImage className="h-8 w-8 text-blue-500" />
  }
  if (["pdf"].includes(extension)) {
    return <FileText className="h-8 w-8 text-red-500" />
  }
  if (["doc", "docx"].includes(extension)) {
    return <FileText className="h-8 w-8 text-sky-600" />
  }
  if (["xls", "xlsx", "csv"].includes(extension)) {
    return <FileText className="h-8 w-8 text-green-600" />
  }
  if (["ppt", "pptx"].includes(extension)) {
    return <FileText className="h-8 w-8 text-orange-500" />
  }
  if (["mp3", "wav", "ogg"].includes(extension)) {
    return <FileAudio className="h-8 w-8 text-purple-500" />
  }
  if (["mp4", "mov", "avi", "mkv"].includes(extension)) {
    return <FileVideo className="h-8 w-8 text-pink-500" />
  }
  if (["zip", "rar", "tar", "gz"].includes(extension)) {
    return <FileArchive className="h-8 w-8 text-yellow-500" />
  }
  return <FileQuestion className="h-8 w-8 text-light-text-secondary dark:text-dark-text-secondary" />
}

// --- Emoji Data ---
const emojis = [
  "😀",
  "😃",
  "😄",
  "😁",
  "😆",
  "😅",
  "😂",
  "🤣",
  "😊",
  "😇",
  "🙂",
  "🙃",
  "😉",
  "😌",
  "😍",
  "🥰",
  "😘",
  "😗",
  "😙",
  "😚",
  "😋",
  "😛",
  "😝",
  "😜",
  "🤪",
  "🤨",
  "🧐",
  "🤓",
  "😎",
  "🤩",
  "🥳",
  "😏",
  "😒",
  "😞",
  "😔",
  "😟",
  "😕",
  "🙁",
  "☹️",
  "😣",
  "👍",
  "👎",
  "👌",
  "✌️",
  "🤞",
  "🤟",
  "🤘",
  "🤙",
  "👈",
  "👉",
  "👆",
  "👇",
  "☝️",
  "👋",
  "🤚",
  "🖐️",
  "✋",
  "🖖",
  "👏",
  "🙌",
  "🤝",
  "🙏",
  "✍️",
  "💪",
  "🦾",
  "🦿",
  "🦵",
  "🦶",
  "👂",
  "👃",
  "❤️",
  "🧡",
  "💛",
  "💚",
  "💙",
  "💜",
  "🖤",
  "🤍",
  "🤎",
  "💔",
  "❣️",
  "💕",
  "💞",
  "💓",
  "💗",
  "💖",
  "💘",
  "💝",
  "💟",
  "☮️",
]

// --- Mock Data & Interfaces ---
interface ChatListItem {
  id: string
  name: string
  avatar?: string
  lastMessage: string
  time: string
  unreadCount?: number
  isOnline?: boolean
  isTyping?: boolean
  type: "direct" | "group"
  members?: GroupMember[]
}

interface GroupMember {
  id: string
  name: string
  avatar?: string
  isOnline?: boolean
}

interface Attachment {
  id: string
  name: string
  type: "image" | "pdf" | "doc" | "audio" | "video" | "archive" | "other"
  url: string
  previewUrl?: string
  size?: number
}

interface VoiceNote {
  id: string
  url: string
  duration: number
}

interface Poll {
  id: string
  question: string
  type: "single" | "multiple" | "yesno" | "custom"
  options: { id: string; text: string; votes: number }[]
  allowCustomOptions: boolean
  totalVotes: number
  userVote?: string[]
}

interface Task {
  id: string
  title: string
  description?: string
  assignedTo: string[]
  startDate?: string
  dueDate?: string
  status: "not-started" | "in-progress" | "ongoing" | "completed"
  priority?: "low" | "normal" | "high"
  reminder?: boolean
  reminderDateTime?: string
  completion?: number
  createdBy: string
}

interface Event {
  id: string
  title: string
  description?: string
  date: string
  time?: string
  isAllDay: boolean
  isRepeating: boolean
  reminder?: string
}

// --- Support chat message (third tab) -----------------------------
// A lightweight message shape for the always-on support conversation.
// Distinct from the feature-rich `Message` used by Direct/Group chats.
interface SupportMessage {
  id: string
  text: string
  sender: "user" | "support"
  timestamp: Date
}

interface Message {
  id: string
  sender: string
  senderName?: string
  avatar?: string
  content?: string
  attachments?: Attachment[]
  voiceNote?: VoiceNote
  poll?: Poll
  task?: Task
  event?: Event
  timestamp: string
  status?: "sent" | "delivered" | "read"
  replyTo?: { messageId: string; content: string; sender: string }
  isEdited?: boolean
  editedAt?: string
  mentions?: string[]
  reactions?: { emoji: string; users: string[] }[]
  // ── Parity with /communication/chat ─────────────────────────────
  /** When set, message stays at the top of the chat until this date. */
  isPinned?: boolean
  pinnedUntil?: Date
  /** When set, the message is a SCHEDULED message awaiting send. */
  scheduledFor?: Date
  /** When true, message was sent with urgent-notification pulses. */
  isUrgent?: boolean
  /** Inline appointment confirmation card payload. */
  appointment?: {
    purpose: string
    hostName: string
    day: string
    slotLabel: string
    slotStart: string
    slotEnd: string
    apptType: string
    notes?: string
    visitorName?: string
  }
  /** Inline document reference (rendered as a chip). */
  referencedDocument?: {
    docType: string
    id: string
    label?: string
  }
}

/**
 * Format a BE `ChatMessage` payload into the FE `Message` shape this
 * popup renders. Timestamps are rendered as `HH:mm`; the sender id
 * doubles as the FE `sender` field, with `"me"` reserved for the
 * authenticated user so the bubble flips to the sender variant.
 */
function beMessageToFe(m: BEChatMessage, meUserId: string): Message {
  const isMine = m.senderUserId === meUserId
  const timestamp = new Date(m.createdAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })
  const attachments: Attachment[] | undefined = m.attachments?.map((a) => ({
    id: a.id,
    name: a.filename,
    type: a.mimetype.startsWith("image/")
      ? "image"
      : a.mimetype === "application/pdf"
        ? "pdf"
        : a.mimetype.startsWith("audio/")
          ? "audio"
          : a.mimetype.startsWith("video/")
            ? "video"
            : "other",
    url: a.storagePath,
    size: a.sizeBytes,
  }))
  const reactions = m.reactions?.length
    ? (() => {
        const buckets: Record<string, string[]> = {}
        for (const r of m.reactions!) {
          const bucket = (buckets[r.emoji] ||= [])
          const label = r.user?.name || r.userId
          if (!bucket.includes(label)) bucket.push(label)
        }
        return Object.entries(buckets).map(([emoji, users]) => ({ emoji, users }))
      })()
    : undefined
  return {
    id: m.id,
    sender: isMine ? "me" : m.sender?.name || m.senderUserId,
    senderName: m.sender?.name || undefined,
    avatar: m.sender?.avatar || undefined,
    content: m.content || undefined,
    attachments,
    reactions,
    timestamp,
    status: isMine ? "sent" : undefined,
    isEdited: !!m.editedAt,
    editedAt: m.editedAt
      ? new Date(m.editedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : undefined,
    replyTo: m.replyTo
      ? {
          messageId: m.replyTo.id,
          content: m.replyTo.content,
          sender: m.replyTo.sender?.name || "Someone",
        }
      : undefined,
    isUrgent: !!m.isUrgent && !m.urgentAcknowledgedAt,
  }
}

interface ChatPopupProps {
  onClose: () => void
}

export default function ChatPopup({ onClose }: ChatPopupProps) {
  const [activeTab, setActiveTab] = useState("direct")
  const [selectedChat, setSelectedChat] = useState<ChatListItem | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [messageInput, setMessageInput] = useState("")
  // ── BE-wired message store ───────────────────────────────────────
  // Replaces the module-level `mockMessages` array. Messages for the
  // active conversation live here; sends/edits/deletes go through the
  // chatService helpers and this state updates when the WS echoes.
  const [messages, setMessages] = useState<Message[]>([])
  const [currentUserId, setCurrentUserId] = useState<string>("me")
  const [socketReady, setSocketReady] = useState(false)
  // Group member picker (used for @-mentions) — hydrated from the
  // conversation's participants when the popup opens one. No BE for
  // avatars/online status today, so these stay best-effort.
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([])
  // BE-hydrated conversation lists. `null` = still loading / fetch
  // failed; empty array = tenant has no chats yet.
  const [beDirectChats, setBeDirectChats] = useState<ChatListItem[] | null>(null)
  const [beGroupChats, setBeGroupChats] = useState<ChatListItem[] | null>(null)
  const [filesToPreview, setFilesToPreview] = useState<Attachment[]>([])
  const [isRecording, setIsRecording] = useState(false)
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null)
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null)
  const [replyingTo, setReplyingTo] = useState<Message | null>(null)
  const [editingMessage, setEditingMessage] = useState<string | null>(null)
  // ── Parity additions imported from /communication/chat ──────────
  /** Inline edit textarea content while editing a message. */
  const [editContent, setEditContent] = useState("")
  /** Schedule-message dialog state. */
  const [showScheduleDialog, setShowScheduleDialog] = useState(false)
  const [scheduleDateTime, setScheduleDateTime] = useState("")
  const [scheduleDraft, setScheduleDraft] = useState("")
  /** Reference Transaction dialog (cascading DocType -> Tx). */
  const [showTransactionDialog, setShowTransactionDialog] = useState(false)
  const [transactionDocType, setTransactionDocType] = useState("")
  const [transactionId, setTransactionId] = useState("")
  /** Reference Document dialog. */
  const [showDocumentDialog, setShowDocumentDialog] = useState(false)
  const [documentDocType, setDocumentDocType] = useState("")
  const [documentId, setDocumentId] = useState("")
  /** Pin-message dialog (with expiry duration). */
  const [showPinDialog, setShowPinDialog] = useState(false)
  const [pinTargetMessageId, setPinTargetMessageId] = useState<string | null>(null)
  const [pinDuration, setPinDuration] = useState<"30" | "60" | "90">("30")
  /** Urgent-notifications scheduler (interval id + content). */
  const [urgentMode, setUrgentMode] = useState(false)
  const urgentIntervalRef = useRef<NodeJS.Timeout | null>(null)
  /** Voice-to-text dictation state. */
  const [isDictating, setIsDictating] = useState(false)
  const dictationRecognitionRef = useRef<any>(null)
  /**
   * Custom chat background + preset are pulled from the shared
   * ChatContext so the popup stays in lock-step with the in-page
   * /communication/chat module — uploading or clearing a background
   * in either surface immediately reflects in the other.
   */
  const {
    chatBackground,
    setChatBackground,
    customChatBackground,
    setCustomChatBackground,
    customChatBackgroundOpacity,
    setCustomChatBackgroundOpacity,
  } = useChat()
  // (bumpMessageVersion removed — all mutations now flow through
  //  `setMessages` so React handles re-renders directly.)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showMentions, setShowMentions] = useState(false)
  const [mentionQuery, setMentionQuery] = useState("")
  const [cursorPosition, setCursorPosition] = useState(0)
  const [selectedMessages, setSelectedMessages] = useState<string[]>([])
  const [isSelectionMode, setIsSelectionMode] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [isTyping, setIsTyping] = useState(false)
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  // ── Support chat (third tab) ────────────────────────────────────
  // A single always-on conversation with the support team. Unlike
  // Direct/Group there is no chat list — the tab opens straight into
  // the conversation, with its own message input rendered in the
  // footer. Wire `handleSendSupportMessage` to a real support desk /
  // ticketing backend to replace the simulated acknowledgement.
  const [supportMessages, setSupportMessages] = useState<SupportMessage[]>([
    {
      id: "sup-1",
      text: "Hello! How can I help you today?",
      sender: "support",
      timestamp: new Date(),
    },
  ])
  const [supportInput, setSupportInput] = useState("")
  const supportMessagesEndRef = useRef<HTMLDivElement>(null)

  // New states for chat selection and management
  const [selectedChats, setSelectedChats] = useState<string[]>([])
  const [isChatSelectionMode, setIsChatSelectionMode] = useState(false)
  const [showNewChatDialog, setShowNewChatDialog] = useState(false)
  const [newChatType, setNewChatType] = useState<"direct" | "group">("direct")
  const [newChatForm, setNewChatForm] = useState({
    name: "",
    description: "",
    members: [] as string[],
  })

  // Dialog states
  const [showPollDialog, setShowPollDialog] = useState(false)
  const [showTaskDialog, setShowTaskDialog] = useState(false)
  const [showEventDialog, setShowEventDialog] = useState(false)
  const [showGroupSettingsDialog, setShowGroupSettingsDialog] = useState(false)

  // Form states
  const [pollForm, setPollForm] = useState({
    question: "",
    type: "single" as "single" | "multiple" | "yesno" | "custom",
    options: ["", ""],
    allowCustomOptions: false,
  })

  // Task form mirrors the Create Task dialog used by /academics/events-calendar
  // (parent-calendar) — start/due dates, status, priority, reminder,
  // recurrence/assign action buttons and a completion slider.
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    assignedTo: [] as string[],
    startDate: "",
    dueDate: "",
    status: "not-started" as "not-started" | "in-progress" | "completed",
    priority: "normal" as "low" | "normal" | "high",
    reminder: true,
    reminderDateTime: "",
    completion: 0,
  })

  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    date: "",
    time: "",
    isAllDay: false,
    isRepeating: false,
    reminder: "",
  })

  // Chat Settings dialog (links to the Chat Settings function in
  // /communication/chat — same items: pin/mute/forward/etc).
  const [showChatSettingsDialog, setShowChatSettingsDialog] = useState(false)

  // Group Settings State (mirrors /communication/chat).
  const [allowAllMembersToSendMessage, setAllowAllMembersToSendMessage] = useState(true)
  const [groupPermissions, setGroupPermissions] = useState<{
    allowInitiateChat: boolean
    allowRespondToChat: boolean
    allowEditChat: boolean
    allowDeleteChat: boolean
    exemptedUsers: string[]
    exemptedUserTypes: string[]
  }>({
    allowInitiateChat: true,
    allowRespondToChat: true,
    allowEditChat: true,
    allowDeleteChat: true,
    exemptedUsers: [],
    exemptedUserTypes: [],
  })
  const [showAddExemptUserDialog, setShowAddExemptUserDialog] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const audioPreviewPlayerRef = useRef<HTMLAudioElement>(null)
  const messageInputRef = useRef<HTMLInputElement>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Click outside handler to close menus - MOVED TO TOP
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const menus = document.querySelectorAll('[id^="chat-menu-"], [id^="msg-menu-"], #input-options-menu')
      menus.forEach((menu) => {
        if (menu instanceof HTMLElement && menu.style.display === "block") {
          const target = event.target as Node
          if (
            !menu.contains(target) &&
            !target.parentElement?.classList.contains("h-6") &&
            !target.parentElement?.classList.contains("h-3")
          ) {
            menu.style.display = "none"
          }
        }
      })
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  useEffect(() => {
    return () => {
      filesToPreview.forEach((file) => {
        if (file.previewUrl && file.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(file.previewUrl)
        }
      })
      if (recordedAudioUrl && recordedAudioUrl.startsWith("blob:")) {
        URL.revokeObjectURL(recordedAudioUrl)
      }
    }
  }, [filesToPreview, recordedAudioUrl])

  // Handle typing indicator
  useEffect(() => {
    if (messageInput.trim() && selectedChat) {
      setIsTyping(true)
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false)
      }, 2000)
    }
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [messageInput, selectedChat])

  // Auto-scroll the support conversation to the newest message
  // whenever it changes (and only while the Support tab is active).
  useEffect(() => {
    if (activeTab === "support") {
      supportMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }
  }, [supportMessages, activeTab])

  // ─── BE: hydrate conversation lists + connect WS ─────────────────
  useEffect(() => {
    let cancelled = false
    listConversations()
      .then((rows) => {
        if (cancelled) return
        const direct: ChatListItem[] = []
        const group: ChatListItem[] = []
        for (const c of rows) {
          const participants = c.participants ?? []
          const isGroup = c.type === "Group"
          const other = isGroup
            ? null
            : participants.find((p) => !!p.user) ?? null
          const name =
            c.title ||
            other?.user?.name ||
            (isGroup ? "Group chat" : "Direct chat")
          const avatar =
            (!isGroup ? other?.user?.avatar : null) ||
            "/placeholder.svg?width=40&height=40"
          const members: GroupMember[] | undefined = isGroup
            ? participants
                .filter((p) => !!p.user)
                .map((p) => ({
                  id: p.userId,
                  name: p.user!.name,
                  avatar: p.user!.avatar || undefined,
                  isOnline: false,
                }))
            : undefined
          const item: ChatListItem = {
            id: c.id,
            name,
            avatar,
            lastMessage: "",
            time: c.lastMessageAt
              ? new Date(c.lastMessageAt).toLocaleString(undefined, {
                  hour: "numeric",
                  minute: "2-digit",
                })
              : "",
            unreadCount: typeof (c as any).unreadCount === "number"
              ? (c as any).unreadCount
              : undefined,
            isOnline: false,
            type: isGroup ? "group" : "direct",
            members,
          }
          if (isGroup) group.push(item)
          else direct.push(item)
        }
        setBeDirectChats(direct)
        setBeGroupChats(group)
      })
      .catch((err) => {
        console.error("[chat-popup] failed to load conversations", err)
        if (!cancelled) {
          setBeDirectChats([])
          setBeGroupChats([])
        }
      })

    const token = readAuthToken()
    if (token) {
      connectChatSocket(token)
        .then(() => {
          if (!cancelled) setSocketReady(true)
        })
        .catch((err) => {
          console.error("[chat-popup] socket connect failed", err)
        })
    }

    return () => {
      cancelled = true
      disconnectChatSocket()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── BE: pull the authenticated user id (for "me" sender check) ──
  useEffect(() => {
    try {
      const raw =
        sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        null
      if (!raw) return
      const parsed = JSON.parse(raw)
      const id = parsed?.id || parsed?.sub || parsed?.userId
      if (id && typeof id === "string") setCurrentUserId(id)
    } catch {
      /* best-effort — "me" fallback is harmless */
    }
  }, [])

  // ─── BE: load messages + subscribe when a chat is opened ─────────
  useEffect(() => {
    if (!selectedChat) {
      setMessages([])
      setGroupMembers([])
      return
    }
    setGroupMembers(selectedChat.members ?? [])
    let cancelled = false
    ;(async () => {
      try {
        const res = await beListMessages(selectedChat.id, { limit: 50 })
        if (cancelled) return
        const fe = res.items.map((m) => beMessageToFe(m, currentUserId))
        setMessages([...fe].reverse())
        try {
          await beMarkRead(selectedChat.id)
        } catch {
          /* silent */
        }
      } catch (err) {
        console.error("[chat-popup] failed to load messages", err)
      }
    })()
    if (socketReady) {
      subscribeToConversation(selectedChat.id)
    }
    const activeId = selectedChat.id
    return () => {
      cancelled = true
      if (socketReady) unsubscribeFromConversation(activeId)
    }
  }, [selectedChat?.id, socketReady, currentUserId])

  // ─── BE: global socket listeners for the active conversation ─────
  useEffect(() => {
    if (!socketReady) return
    const offNew = onChatEvent<BEChatMessage>("message:new", (payload) => {
      if (!selectedChat || payload.conversationId !== selectedChat.id) return
      setMessages((prev) => {
        if (prev.some((m) => m.id === payload.id)) return prev
        return [...prev, beMessageToFe(payload, currentUserId)]
      })
    })
    const offEdited = onChatEvent<BEChatMessage>("message:edited", (payload) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === payload.id
            ? {
                ...m,
                content: payload.content || m.content,
                isEdited: true,
                editedAt: payload.editedAt
                  ? new Date(payload.editedAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : m.editedAt,
              }
            : m,
        ),
      )
    })
    const offDeleted = onChatEvent<{ id: string; conversationId: string }>(
      "message:deleted",
      (payload) => {
        setMessages((prev) => prev.filter((m) => m.id !== payload.id))
      },
    )
    const offRxAdded = onChatEvent<{
      messageId: string
      emoji: string
      userId: string
    }>("reaction:added", ({ messageId, emoji, userId }) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m
          const reactions = [...(m.reactions || [])]
          const existing = reactions.find((r) => r.emoji === emoji)
          const label = userId === currentUserId ? "me" : userId
          if (existing) {
            if (!existing.users.includes(label))
              existing.users = [...existing.users, label]
          } else {
            reactions.push({ emoji, users: [label] })
          }
          return { ...m, reactions }
        }),
      )
    })
    const offRxRemoved = onChatEvent<{
      messageId: string
      emoji: string
      userId: string
    }>("reaction:removed", ({ messageId, emoji, userId }) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m
          const label = userId === currentUserId ? "me" : userId
          const reactions = (m.reactions || [])
            .map((r) =>
              r.emoji === emoji
                ? { ...r, users: r.users.filter((u) => u !== label) }
                : r,
            )
            .filter((r) => r.users.length > 0)
          return { ...m, reactions }
        }),
      )
    })
    const offUrgentStarted = onChatEvent<{ id: string }>(
      "message:urgent-started",
      (p) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === p.id ? { ...m, isUrgent: true } : m)),
        )
      },
    )
    const offUrgentAcked = onChatEvent<{ id: string }>(
      "message:urgent-acknowledged",
      (p) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === p.id ? { ...m, isUrgent: false } : m)),
        )
      },
    )
    return () => {
      offNew()
      offEdited()
      offDeleted()
      offRxAdded()
      offRxRemoved()
      offUrgentStarted()
      offUrgentAcked()
    }
  }, [socketReady, selectedChat?.id, currentUserId])

  // New function to handle @ mentions properly
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      const position = e.target.selectionStart || 0

      setMessageInput(value)

      // Handle @ mentions
      const beforeCursor = value.substring(0, position)
      const atIndex = beforeCursor.lastIndexOf("@")

      if (atIndex !== -1) {
        const afterAt = beforeCursor.substring(atIndex + 1)
        if (!afterAt.includes(" ") && selectedChat?.type === "group") {
          setShowMentions(true)
          setMentionQuery(afterAt)
          setCursorPosition(position)
        } else {
          setShowMentions(false)
        }
      } else {
        setShowMentions(false)
      }
    },
    [selectedChat?.type],
  )

  const handleMentionSelect = useCallback((member: GroupMember) => {
    const input = messageInputRef.current
    if (!input) return

    const value = input.value
    const position = input.selectionStart || 0
    const beforeCursor = value.substring(0, position)
    const atIndex = beforeCursor.lastIndexOf("@")

    if (atIndex !== -1) {
      const beforeAt = value.substring(0, atIndex)
      const afterCursor = value.substring(position)
      const newValue = `${beforeAt}@${member.name} ${afterCursor}`

      setMessageInput(newValue)
      setShowMentions(false)

      setTimeout(() => {
        const newPosition = atIndex + member.name.length + 2
        input.setSelectionRange(newPosition, newPosition)
        input.focus()
      }, 0)
    }
  }, [])

  /**
   * Support chat send handler. Appends the user's message and then
   * a simulated support acknowledgement. Swap the setTimeout block
   * for a real support-desk / socket call when wiring the backend.
   */
  const handleSendSupportMessage = () => {
    if (!supportInput.trim()) return
    const userMessage: SupportMessage = {
      id: `sup-${Date.now()}`,
      text: supportInput.trim(),
      sender: "user",
      timestamp: new Date(),
    }
    setSupportMessages((prev) => [...prev, userMessage])
    setSupportInput("")

    setTimeout(() => {
      setSupportMessages((prev) => [
        ...prev,
        {
          id: `sup-${Date.now() + 1}`,
          text: "Thank you for your message. Our support team will get back to you shortly.",
          sender: "support",
          timestamp: new Date(),
        },
      ])
    }, 1000)
  }

  // New functions for chat management
  const handleChatAction = (chatId: string, action: string) => {
    switch (action) {
      case "select":
        setIsChatSelectionMode(true)
        setSelectedChats([chatId])
        break
      case "reply": {
        const chat =
          (beDirectChats ?? []).find((c) => c.id === chatId) ??
          (beGroupChats ?? []).find((c) => c.id === chatId) ??
          null
        if (chat) setSelectedChat(chat)
        break
      }
      case "forward":
        setIsChatSelectionMode(true)
        setSelectedChats([chatId])
        break
      case "delete":
        console.log("Delete chat:", chatId)
        break
      case "copy":
        console.log("Copy chat:", chatId)
        break
      case "pin":
        console.log("Pin chat:", chatId)
        break
      case "start":
        console.log("Start chat:", chatId)
        break
    }
  }

  const handleBulkChatAction = (action: "delete" | "forward") => {
    console.log(`Bulk ${action} chats:`, selectedChats)
    setSelectedChats([])
    setIsChatSelectionMode(false)
  }

  const handleNewChat = () => {
    if (newChatType === "direct") {
      console.log("Creating new direct chat with:", newChatForm.name)
    } else {
      console.log("Creating new group chat:", newChatForm)
    }
    setShowNewChatDialog(false)
    setNewChatForm({ name: "", description: "", members: [] })
  }

  const handleGroupSettings = (action: string) => {
    switch (action) {
      case "info":
        setShowGroupSettingsDialog(true)
        break
      case "settings":
        console.log("Group settings")
        break
      case "add-members":
        console.log("Add members")
        break
      case "mute":
        console.log("Mute notifications")
        break
      case "leave":
        console.log("Leave group")
        break
    }
  }

  const filteredMembers =
    selectedChat?.members?.filter((member) => member.name.toLowerCase().includes(mentionQuery.toLowerCase())) || []

  const handleStartRecording = () => {
    setIsRecording(true)
    setRecordingDuration(0)
    setRecordedAudio(null)
    setRecordedAudioUrl(null)
    console.log("Simulating recording start...")
    recordingIntervalRef.current = setInterval(() => {
      setRecordingDuration((prev) => prev + 1)
    }, 1000)
  }

  const handleStopRecording = () => {
    setIsRecording(false)
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current)
    }
    console.log("Simulating recording stop. Duration:", recordingDuration)
    const mockBlob = new Blob([new Uint8Array(1024 * 10)], { type: "audio/webm;codecs=opus" })
    setRecordedAudio(mockBlob)
    setRecordedAudioUrl(URL.createObjectURL(mockBlob))
  }

  const handleCancelRecording = () => {
    setIsRecording(false)
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current)
    }
    setRecordingDuration(0)
    setRecordedAudio(null)
    if (recordedAudioUrl && recordedAudioUrl.startsWith("blob:")) {
      URL.revokeObjectURL(recordedAudioUrl)
    }
    setRecordedAudioUrl(null)
    console.log("Recording cancelled.")
  }

  const handleDeleteRecordedAudio = () => {
    if (recordedAudioUrl && recordedAudioUrl.startsWith("blob:")) {
      URL.revokeObjectURL(recordedAudioUrl)
    }
    setRecordedAudio(null)
    setRecordedAudioUrl(null)
    setRecordingDuration(0)
    console.log("Recorded audio deleted.")
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files) {
      const newFilePreviews: Attachment[] = Array.from(files).map((file, index) => {
        const fileType = file.type.split("/")[0]
        let attachmentType: Attachment["type"] = "other"
        if (fileType === "image") attachmentType = "image"
        else if (file.type === "application/pdf") attachmentType = "pdf"

        return {
          id: `preview-${Date.now()}-${index}`,
          name: file.name,
          type: attachmentType,
          url: "#",
          previewUrl: fileType === "image" ? URL.createObjectURL(file) : undefined,
          size: file.size,
        }
      })
      setFilesToPreview((prev) => [...prev, ...newFilePreviews])
    }
    if (event.target) {
      event.target.value = ""
    }
  }

  const handleRemoveFilePreview = (fileId: string) => {
    const fileToRemove = filesToPreview.find((f) => f.id === fileId)
    if (fileToRemove?.previewUrl && fileToRemove.previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(fileToRemove.previewUrl)
    }
    setFilesToPreview((prev) => prev.filter((file) => file.id !== fileId))
  }

  const handleSelectChat = (chat: ChatListItem) => {
    // Save draft for current chat
    if (selectedChat && messageInput.trim()) {
      setDrafts((prev) => ({ ...prev, [selectedChat.id]: messageInput }))
    }

    setSelectedChat(chat)
    setFilesToPreview([])
    setReplyingTo(null)
    setEditingMessage(null)
    setIsSelectionMode(false)
    setSelectedMessages([])
    handleCancelRecording()

    // Load draft for new chat
    const draft = drafts[chat.id]
    if (draft) {
      setMessageInput(draft)
    } else {
      setMessageInput("")
    }
  }

  const handleSendMessage = async () => {
    if (messageInput.trim() === "" && filesToPreview.length === 0 && !recordedAudio) return
    if (!selectedChat) return

    const text = messageInput.trim()
    const replyToId = replyingTo?.id

    // Clear composer immediately so the user can keep typing. WS echo
    // appends the authoritative row; we don't optimistic-insert since
    // the BE normalizes the id.
    setMessageInput("")
    setFilesToPreview([])
    setRecordedAudio(null)
    setRecordingDuration(0)
    setIsRecording(false)
    setReplyingTo(null)
    if (selectedChat) {
      setDrafts((prev) => {
        const n = { ...prev }
        delete n[selectedChat.id]
        return n
      })
    }

    try {
      const persisted = await beSendMessage(selectedChat.id, {
        content: text,
        contentType: "Text",
        replyToId,
      })
      // Fallback append in case we haven't received the WS echo yet
      // (dev without socket, or a race on first subscribe).
      setMessages((prev) => {
        if (prev.some((m) => m.id === persisted.id)) return prev
        return [...prev, beMessageToFe(persisted, currentUserId)]
      })
      // V5 §1.11 #7 — urgent flag piggy-backs on the compose state.
      if (urgentMode) {
        try {
          await markChatMessageUrgent(persisted.id, {})
        } catch (err) {
          console.error("[chat-popup] mark urgent failed", err)
        }
      }
    } catch (err) {
      console.error("[chat-popup] send failed", err)
    }
  }

  const handleCreatePoll = () => {
    if (!pollForm.question.trim()) return

    const poll: Poll = {
      id: `poll-${Date.now()}`,
      question: pollForm.question,
      type: pollForm.type,
      options:
        pollForm.type === "yesno"
          ? [
            { id: "yes", text: "Yes", votes: 0 },
            { id: "no", text: "No", votes: 0 },
          ]
          : pollForm.options
            .filter((opt) => opt.trim())
            .map((opt, idx) => ({
              id: `opt-${idx}`,
              text: opt.trim(),
              votes: 0,
            })),
      allowCustomOptions: pollForm.allowCustomOptions,
      totalVotes: 0,
    }

    const newMessage: Message = {
      id: `local-poll-${Date.now()}`,
      sender: "me",
      poll,
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      status: "sent",
    }

    // Polls aren't persisted by the BE yet — hold them in local state
    // so the current popup session renders them. They vanish on
    // reload. [[chat-popup-local-only-features]]
    setMessages((prev) => [...prev, newMessage])
    setShowPollDialog(false)
    setPollForm({ question: "", type: "single", options: ["", ""], allowCustomOptions: false })
  }

  const handleCreateTask = () => {
    if (!taskForm.title.trim() || !taskForm.dueDate) return

    const task: Task = {
      id: `task-${Date.now()}`,
      title: taskForm.title,
      description: taskForm.description || undefined,
      assignedTo: taskForm.assignedTo,
      startDate: taskForm.startDate || undefined,
      dueDate: taskForm.dueDate || undefined,
      status: taskForm.status,
      priority: taskForm.priority,
      reminder: taskForm.reminder,
      reminderDateTime: taskForm.reminderDateTime || undefined,
      completion: taskForm.completion,
      createdBy: "me",
    }

    const newMessage: Message = {
      id: `local-task-${Date.now()}`,
      sender: "me",
      task,
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      status: "sent",
    }

    // Local-only — BE has no task-in-chat doctype.
    setMessages((prev) => [...prev, newMessage])
    setShowTaskDialog(false)
    setTaskForm({
      title: "",
      description: "",
      assignedTo: [],
      startDate: "",
      dueDate: "",
      status: "not-started",
      priority: "normal",
      reminder: true,
      reminderDateTime: "",
      completion: 0,
    })
  }

  const handleCreateEvent = () => {
    if (!eventForm.title.trim() || !eventForm.date) return

    const event: Event = {
      id: `event-${Date.now()}`,
      title: eventForm.title,
      description: eventForm.description || undefined,
      date: eventForm.date,
      time: eventForm.isAllDay ? undefined : eventForm.time,
      isAllDay: eventForm.isAllDay,
      isRepeating: eventForm.isRepeating,
      reminder: eventForm.reminder || undefined,
    }

    const newMessage: Message = {
      id: `local-event-${Date.now()}`,
      sender: "me",
      event,
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      status: "sent",
    }

    // Local-only — BE has no event-in-chat doctype.
    setMessages((prev) => [...prev, newMessage])
    setShowEventDialog(false)
    setEventForm({ title: "", description: "", date: "", time: "", isAllDay: false, isRepeating: false, reminder: "" })
  }

  const handleReaction = async (messageId: string, emoji: string) => {
    const target = messages.find((m) => m.id === messageId)
    if (!target) return
    // Local-only messages (polls/tasks/events) are optimistic.
    const isLocal = target.id.startsWith("local-")
    const hasMine = target.reactions?.find((r) => r.emoji === emoji)?.users.includes("me")
    // Optimistic update — WS echo (or local fallback) will converge.
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId) return m
        const reactions = [...(m.reactions || [])]
        const existing = reactions.find((r) => r.emoji === emoji)
        if (existing) {
          if (existing.users.includes("me")) {
            existing.users = existing.users.filter((u) => u !== "me")
          } else {
            existing.users = [...existing.users, "me"]
          }
        } else {
          reactions.push({ emoji, users: ["me"] })
        }
        return { ...m, reactions: reactions.filter((r) => r.users.length > 0) }
      }),
    )
    if (isLocal) return
    try {
      if (hasMine) {
        await beRemoveReaction(messageId, emoji)
      } else {
        await beAddReaction(messageId, emoji)
      }
    } catch (err) {
      console.error("[chat-popup] reaction failed", err)
    }
  }

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content)
    console.log("Message copied to clipboard")
  }

  const handleDeleteMessage = async (messageId: string) => {
    const isLocal = messageId.startsWith("local-") || messageId.startsWith("sched-")
    // Optimistic — WS echo will remove from other sessions. For local
    // messages (polls/tasks/events/scheduled) the only source of truth
    // is this component's state, so the local removal is final.
    setMessages((prev) => prev.filter((m) => m.id !== messageId))
    if (isLocal) return
    try {
      await beDeleteMessage(messageId)
    } catch (err) {
      console.error("[chat-popup] delete failed", err)
    }
  }

  const handleForwardMessage = (messageId: string) => {
    console.log("Forward message:", messageId)
    // Implementation for forwarding
  }

  const handleBulkDelete = async () => {
    const ids = [...selectedMessages]
    setMessages((prev) => prev.filter((m) => !ids.includes(m.id)))
    setSelectedMessages([])
    setIsSelectionMode(false)
    for (const messageId of ids) {
      if (messageId.startsWith("local-") || messageId.startsWith("sched-")) continue
      try {
        await beDeleteMessage(messageId)
      } catch (err) {
        console.error("[chat-popup] bulk delete failed for", messageId, err)
      }
    }
  }

  const renderChatListItem = (chat: ChatListItem) => (
    <div key={chat.id} className="relative group">
      <div
        className={`flex items-center p-3 hover:bg-primary-light dark:hover:bg-opacity-10 rounded-lg cursor-pointer transition-colors ${selectedChat?.id === chat.id ? "bg-primary-light dark:bg-opacity-10" : ""}`}
        onClick={() => !isChatSelectionMode && handleSelectChat(chat)}
      >
        {isChatSelectionMode && (
          <Checkbox
            checked={selectedChats.includes(chat.id)}
            onCheckedChange={(checked) => {
              if (checked) {
                setSelectedChats((prev) => [...prev, chat.id])
              } else {
                setSelectedChats((prev) => prev.filter((id) => id !== chat.id))
              }
            }}
            className="mr-3"
          />
        )}

        <div className="relative mr-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={chat.avatar || "/placeholder.svg"} alt={chat.name} />
            <AvatarFallback>{chat.name.substring(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          {chat.isOnline && chat.type === "direct" && (
            <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-success ring-2 ring-light-card-bg dark:ring-dark-card-bg" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-center">
            <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary truncate">
              {chat.name}
            </p>
            <div className="flex items-center gap-2">
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">{chat.time}</p>
              {!isChatSelectionMode && (
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      const menu = document.getElementById(`chat-menu-${chat.id}`)
                      if (menu) {
                        menu.style.display = menu.style.display === "none" ? "block" : "none"
                      }
                    }}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                  <div
                    id={`chat-menu-${chat.id}`}
                    className="absolute right-0 top-full mt-1 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50"
                    style={{ display: "none" }}
                  >
                    <div className="py-1" role="menu" aria-orientation="vertical">
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditingMessage(chat.id)
                          document.getElementById(`chat-menu-${chat.id}`)!.style.display = "none"
                        }}
                      >
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit
                      </button>
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleChatAction(chat.id, "reply")
                          document.getElementById(`chat-menu-${chat.id}`)!.style.display = "none"
                        }}
                      >
                        <Reply className="h-4 w-4 mr-2" />
                        Reply
                      </button>
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleChatAction(chat.id, "forward")
                          document.getElementById(`chat-menu-${chat.id}`)!.style.display = "none"
                        }}
                      >
                        <Forward className="h-4 w-4 mr-2" />
                        Forward
                      </button>
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleChatAction(chat.id, "pin")
                          document.getElementById(`chat-menu-${chat.id}`)!.style.display = "none"
                        }}
                      >
                        <Pin className="h-4 w-4 mr-2" />
                        Pin Message
                      </button>
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleChatAction(chat.id, "select")
                          document.getElementById(`chat-menu-${chat.id}`)!.style.display = "none"
                        }}
                      >
                        <CheckSquare className="h-4 w-4 mr-2" />
                        Select
                      </button>
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleChatAction(chat.id, "start")
                          document.getElementById(`chat-menu-${chat.id}`)!.style.display = "none"
                        }}
                      >
                        <Star className="h-4 w-4 mr-2" />
                        Star Message
                      </button>
                      <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleChatAction(chat.id, "delete")
                          document.getElementById(`chat-menu-${chat.id}`)!.style.display = "none"
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-between items-center mt-1">
            <p
              className={`text-xs truncate ${chat.isTyping ? "text-primary italic" : "text-light-text-secondary dark:text-dark-text-secondary"}`}
            >
              {chat.isTyping ? "typing..." : chat.lastMessage}
            </p>
            {chat.unreadCount && chat.unreadCount > 0 && (
              <Badge variant="destructive" className="h-5 px-1.5 text-xs">
                {chat.unreadCount}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  const renderMessageStatus = (status?: "sent" | "delivered" | "read") => {
    if (status === "read") return <CheckCheck className="h-4 w-4 text-emerald-500" />
    if (status === "delivered")
      return <CheckCheck className="h-4 w-4 text-light-text-secondary dark:text-dark-text-secondary" />
    if (status === "sent") return <Check className="h-4 w-4 text-light-text-secondary dark:text-dark-text-secondary" />
    return null
  }

  const renderAttachment = (attachment: Attachment) => {
    if (attachment.type === "image") {
      return (
        <div key={attachment.id} className="mt-2">
          <img src={attachment.url || "/placeholder.svg"}
            alt={attachment.name}
            width={200}
            height={150}
            className="rounded-lg object-cover max-w-xs cursor-pointer border border-divider"
            onClick={() => console.log("Open image preview:", attachment.url)}
          />
          <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">
            {attachment.name} ({attachment.size ? formatFileSize(attachment.size) : ""})
          </p>
        </div>
      )
    }
    return (
      <div
        key={attachment.id}
        className="mt-2 p-2.5 rounded-lg bg-light-bg dark:bg-dark-bg border border-divider flex items-center gap-3 max-w-xs"
      >
        {getFileIcon(attachment.name)}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary truncate">
            {attachment.name}
          </p>
          <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
            {attachment.size ? formatFileSize(attachment.size) : "N/A"}
          </p>
        </div>
        <Button variant="ghost" size="icon" asChild>
          <a href={attachment.url} download={attachment.name} aria-label={`Download ${attachment.name}`}>
            <Download className="h-4 w-4 text-light-text-secondary dark:text-dark-text-secondary" />
          </a>
        </Button>
      </div>
    )
  }

  const VoiceNotePlayer: React.FC<{ voiceNote: VoiceNote; isSender: boolean }> = ({ voiceNote, isSender }) => {
    const [isPlaying, setIsPlaying] = useState(false)
    const [progress, setProgress] = useState(0)
    const [currentTime, setCurrentTime] = useState(0)
    const audioRef = useRef<HTMLAudioElement>(null)
    const isPlaceholder = voiceNote.url === "#mock-audio-placeholder"

    const togglePlayPause = async () => {
      if (isPlaceholder || !audioRef.current) return
      try {
        if (isPlaying) {
          audioRef.current.pause()
        } else {
          await audioRef.current.play()
        }
        setIsPlaying(!isPlaying)
      } catch (error) {
        console.error("Error playing audio:", error)
        setIsPlaying(false)
      }
    }

    useEffect(() => {
      const audio = audioRef.current
      if (isPlaceholder || !audio) return

      const updateAudioState = () => {
        if (audio.duration && Number.isFinite(audio.duration)) {
          setProgress((audio.currentTime / audio.duration) * 100)
          setCurrentTime(audio.currentTime)
        } else {
          setProgress(0)
          setCurrentTime(0)
        }
      }
      const onEnded = () => {
        setIsPlaying(false)
        setProgress(0)
        setCurrentTime(0)
      }

      audio.addEventListener("timeupdate", updateAudioState)
      audio.addEventListener("loadedmetadata", updateAudioState)
      audio.addEventListener("ended", onEnded)

      if (audio.currentSrc !== voiceNote.url) {
        audio.src = voiceNote.url
        audio.load()
      }

      if (audio.readyState >= 2) {
        updateAudioState()
      }

      return () => {
        audio.removeEventListener("timeupdate", updateAudioState)
        audio.removeEventListener("loadedmetadata", updateAudioState)
        audio.removeEventListener("ended", onEnded)
      }
    }, [voiceNote.url, isPlaceholder])

    const formatDuration = (secs: number) => {
      const minutes = Math.floor(secs / 60)
      const seconds = Math.floor(secs % 60)
      return `${minutes}:${String(seconds).padStart(2, "0")}`
    }

    return (
      <div
        className={`flex items-center gap-2 p-2 rounded-lg mt-1.5 w-full max-w-[250px] ${isSender ? "bg-primary/20" : "bg-muted"}`}
      >
        <audio ref={audioRef} preload="metadata" />
        <Button
          variant="ghost"
          size="icon"
          onClick={togglePlayPause}
          className={`h-8 w-8 flex-shrink-0 ${isSender ? "text-primary-foreground/80 hover:text-primary-foreground" : "text-foreground/80 hover:text-foreground"}`}
          disabled={isPlaceholder}
        >
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </Button>
        <div className="flex-1 h-1.5 bg-slate-300 dark:bg-slate-700 rounded-full overflow-hidden relative group">
          <div className="h-full bg-primary rounded-full" style={{ width: `${progress}%` }} />
        </div>
        <span
          className={`text-xs w-12 text-right flex-shrink-0 ${isSender ? "text-primary-foreground/70" : "text-light-text-secondary dark:text-dark-text-secondary"}`}
        >
          {isPlaceholder
            ? formatDuration(voiceNote.duration)
            : isPlaying
              ? formatDuration(currentTime)
              : formatDuration(voiceNote.duration)}
        </span>
      </div>
    )
  }

  const renderPoll = (poll: Poll) => (
    <div className="mt-2 p-3 rounded-lg bg-light-bg dark:bg-dark-bg border border-divider max-w-xs">
      <h4 className="font-medium text-sm mb-2">{poll.question}</h4>
      <div className="space-y-2">
        {poll.options.map((option) => (
          <div
            key={option.id}
            className="flex items-center justify-between p-2 rounded bg-background hover:bg-muted cursor-pointer"
          >
            <span className="text-sm">{option.text}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{option.votes}</span>
              <div className="w-16 h-1 bg-muted rounded-full">
                <div
                  className="h-full bg-primary rounded-full"
                  style={{ width: `${poll.totalVotes > 0 ? (option.votes / poll.totalVotes) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-2">{poll.totalVotes} votes</p>
    </div>
  )

  const renderTask = (task: Task) => {
    const assignedMembers = task.assignedTo.map((id) => groupMembers.find((m) => m.id === id)).filter(Boolean)

    return (
      <div className="mt-2 p-3 rounded-lg bg-light-bg dark:bg-dark-bg border border-divider max-w-xs">
        <div className="flex items-center gap-2 mb-2">
          <ListChecks className="h-4 w-4" />
          <h4 className={`font-medium text-sm ${task.status === "completed" ? "line-through" : ""}`}>{task.title}</h4>
        </div>
        {task.description && <p className="text-xs text-muted-foreground mb-2">{task.description}</p>}
        <div className="flex items-center justify-between text-xs">
          <Badge
            variant={task.status === "completed" ? "default" : task.status === "ongoing" ? "secondary" : "outline"}
          >
            {task.status.replace("-", " ")}
          </Badge>
          {task.dueDate && <span className="text-muted-foreground">{task.dueDate}</span>}
        </div>
        {assignedMembers.length > 0 && (
          <div className="flex items-center gap-1 mt-2">
            <span className="text-xs text-muted-foreground">Assigned to:</span>
            {assignedMembers.map((member) => (
              <Avatar key={member.id} className="h-5 w-5">
                <AvatarImage src={member.avatar || "/placeholder.svg"} />
                <AvatarFallback>{member.name.substring(0, 1)}</AvatarFallback>
              </Avatar>
            ))}
          </div>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="ml-auto">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => console.log("Change status to not-started")}>Not Started</DropdownMenuItem>
            <DropdownMenuItem onClick={() => console.log("Change status to ongoing")}>Ongoing</DropdownMenuItem>
            <DropdownMenuItem onClick={() => console.log("Change status to completed")}>Completed</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )
  }

  const renderEvent = (event: Event) => (
    <div className="mt-2 p-3 rounded-lg bg-light-bg dark:bg-dark-bg border border-divider max-w-xs">
      <div className="flex items-center gap-2 mb-2">
        <Calendar className="h-4 w-4" />
        <h4 className="font-medium text-sm">{event.title}</h4>
      </div>
      {event.description && <p className="text-xs text-muted-foreground mb-2">{event.description}</p>}
      <div className="text-xs text-muted-foreground">
        <p>
          {event.date} {!event.isAllDay && event.time}
        </p>
        {event.isRepeating && <p>Repeating</p>}
      </div>
    </div>
  )

  const renderReactions = (reactions?: { emoji: string; users: string[] }[]) => {
    if (!reactions || reactions.length === 0) return null

    return (
      <div className="flex flex-wrap gap-1 mt-1">
        {reactions.map((reaction, index) => (
          <button
            key={index}
            className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted hover:bg-muted/80 text-xs"
            onClick={() => handleReaction("", reaction.emoji)}
          >
            <span>{reaction.emoji}</span>
            <span>{reaction.users.length}</span>
          </button>
        ))}
      </div>
    )
  }

  // ─── DocType / Transaction registry ────────────────────────────
  // Mirrors the registry used in /communication/chat so the popup
  // can offer the same Reference Transaction / Reference Document
  // experience without duplicating UX. Real installs would source
  // this from metadata; we hard-code a useful default set.
  const DOCTYPES = [
    "Sales Invoice",
    "Payment Receipt",
    "Purchase Order",
    "Quotation",
    "Fee Invoice",
    "Expense Claim",
    "Journal Entry",
  ] as const
  const TRANSACTIONS_BY_DOCTYPE: Record<string, { id: string; label: string }[]> = {
    "Sales Invoice":   [
      { id: "SI-2025-00482", label: "Greenfield Academy — ₦1,250,000" },
      { id: "SI-2025-00481", label: "Riverside School — ₦890,000" },
    ],
    "Payment Receipt": [
      { id: "PR-2025-00219", label: "Tuition fees — Term 2" },
    ],
    "Purchase Order":  [{ id: "PO-2025-00091", label: "Stationery supplies" }],
    "Quotation":       [{ id: "QT-2025-00033", label: "Annual maintenance" }],
    "Fee Invoice":     [{ id: "FI-2025-00701", label: "Adaeze Okonkwo — Term 2 fees" }],
    "Expense Claim":   [{ id: "EC-2025-00045", label: "Field trip reimbursement" }],
    "Journal Entry":   [{ id: "JE-2025-00112", label: "Year-end adjustment" }],
  }

  /**
   * Picks the most natural-sounding installed voice. Prefers en-US
   * neural voices ("Google US English", "Samantha", "Alex"...), then
   * falls back to any en-* voice, then the first available. Returns
   * null if speechSynthesis is unavailable (SSR / Node).
   */
  const pickBestVoice = useCallback((): SpeechSynthesisVoice | null => {
    if (typeof window === "undefined" || !window.speechSynthesis) return null
    const voices = window.speechSynthesis.getVoices()
    if (!voices.length) return null
    const preferredNames = [
      "Google US English",
      "Microsoft Aria Online (Natural) - English (United States)",
      "Samantha",
      "Alex",
      "Karen",
      "Daniel",
    ]
    for (const name of preferredNames) {
      const v = voices.find(vv => vv.name === name)
      if (v) return v
    }
    return voices.find(v => v.lang?.startsWith("en")) ?? voices[0]
  }, [])

  /**
   * Read-aloud helper — replaces the old simple `handleReadAloud`.
   * Cancels any in-progress utterance first so consecutive clicks
   * don't queue up overlapping speech.
   */
  const speakMessage = useCallback((content: string) => {
    if (typeof window === "undefined" || !window.speechSynthesis) return
    window.speechSynthesis.cancel()
    const utterance = new SpeechSynthesisUtterance(content)
    const voice = pickBestVoice()
    if (voice) utterance.voice = voice
    utterance.rate = 1
    utterance.pitch = 1
    window.speechSynthesis.speak(utterance)
  }, [pickBestVoice])

  /** Backwards-compatible alias used by the existing menu wiring. */
  const handleReadAloud = (content: string) => speakMessage(content)

  /**
   * Render-pass parser: turns natural-language dates into clickable
   * spans, `[txn:DocType:ID]` tokens into transaction hyperlinks,
   * and `[doc:DocType:ID]` tokens into document hyperlinks. Plain
   * text segments are returned as-is.
   */
  const parseMessageContent = (content: string) => {
    // First pass: split out [txn:...] and [doc:...] tokens.
    const refRegex = /(\[(?:txn|doc):[^\]]+\])/g
    const segments = content.split(refRegex)
    return segments.flatMap((seg, segIndex) => {
      const txnMatch = seg.match(/^\[txn:([^:]+):([^\]]+)\]$/)
      if (txnMatch) {
        const [, docType, id] = txnMatch
        return (
          <a
            key={`txn-${segIndex}`}
            href={`#/${docType.toLowerCase().replace(/\s+/g, "-")}/${id}`}
            className="inline-flex items-center gap-1 underline text-primary hover:text-primary/80 font-medium"
            onClick={e => { e.preventDefault(); console.log("[v0] Open transaction", docType, id) }}
            title={`${docType} • ${id}`}
          >
            <Hash className="h-3 w-3" />
            {id}
          </a>
        )
      }
      const docMatch = seg.match(/^\[doc:([^:]+):([^\]]+)\]$/)
      if (docMatch) {
        const [, docType, id] = docMatch
        return (
          <a
            key={`doc-${segIndex}`}
            href={`#/${docType.toLowerCase().replace(/\s+/g, "-")}/${id}`}
            className="inline-flex items-center gap-1 underline text-primary hover:text-primary/80 font-medium"
            onClick={e => { e.preventDefault(); console.log("[v0] Open document", docType, id) }}
            title={`${docType} • ${id}`}
          >
            <Link2 className="h-3 w-3" />
            {id}
          </a>
        )
      }
      // Second pass on plain text: linkify natural-language dates.
      const dateRegex =
        /(tomorrow|today|next week|yesterday|next month|last week|last month|january|february|march|april|may|june|july|august|september|october|november|december|\d{1,2}\/\d{1,2}\/\d{4}|\d{1,2}-\d{1,2}-\d{4})/gi
      return seg.split(dateRegex).map((part, idx) => {
        if (part && dateRegex.test(part)) {
          return (
            <span
              key={`d-${segIndex}-${idx}`}
              className="underline cursor-pointer text-primary hover:text-primary/80"
              title={part}
            >
              {part}
            </span>
          )
        }
        return part
      })
    })
  }

  /**
   * Saves an inline message edit. Updates local state optimistically
   * and persists via PATCH /chat/messages/:id; the WS echo overwrites
   * the local copy with the authoritative `editedAt` from the server.
   */
  const handleEditSave = async (messageId: string) => {
    if (!editContent.trim()) return
    const isLocal = messageId.startsWith("local-") || messageId.startsWith("sched-")
    const newContent = editContent.trim()
    setMessages((prev) =>
      prev.map((m) =>
        m.id === messageId
          ? {
              ...m,
              content: newContent,
              isEdited: true,
              editedAt: new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            }
          : m,
      ),
    )
    setEditingMessage(null)
    setEditContent("")
    if (isLocal) return
    try {
      await beEditMessage(messageId, newContent)
    } catch (err) {
      console.error("[chat-popup] edit failed", err)
    }
  }

  /** Open the pin-duration dialog for a given message. */
  const openPinDialog = (messageId: string) => {
    setPinTargetMessageId(messageId)
    setPinDuration("30")
    setShowPinDialog(true)
  }

  /** Confirm pinning the previously-selected message for N days.
   *  Local-only — BE has no pinned-message persistence yet. */
  const confirmPinMessage = () => {
    if (!pinTargetMessageId) return
    const days = Number(pinDuration)
    const until = new Date()
    until.setDate(until.getDate() + days)
    setMessages((prev) =>
      prev.map((m) =>
        m.id === pinTargetMessageId ? { ...m, isPinned: true, pinnedUntil: until } : m,
      ),
    )
    setShowPinDialog(false)
    setPinTargetMessageId(null)
  }

  /** Unpin a previously-pinned message. Local-only. */
  const unpinMessage = (messageId: string) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === messageId ? { ...m, isPinned: false, pinnedUntil: undefined } : m,
      ),
    )
  }

  /**
   * Schedule a message — push it as a regular bubble that carries a
   * `scheduledFor` date so the bubble renders the clock indicator.
   * In a real install this would be queued for server-side delivery.
   */
  const scheduleMessage = () => {
    if (!scheduleDateTime || !scheduleDraft.trim()) return
    const when = new Date(scheduleDateTime)
    // Local-only — BE has no scheduled-message queue for chat yet.
    setMessages((prev) => [
      ...prev,
      {
        id: `sched-${Date.now()}`,
        sender: "me",
        content: scheduleDraft.trim(),
        timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        status: "sent",
        scheduledFor: when,
      },
    ])
    setShowScheduleDialog(false)
    setScheduleDraft("")
    setScheduleDateTime("")
    setMessageInput("")
  }

  /**
   * Trigger urgent-notification pulses every 2 minutes for 20 minutes.
   * Uses the Notification API when permission is granted, otherwise
   * falls back to a console log so the call site never crashes.
   */
  const triggerUrgentNotifications = (content: string) => {
    if (typeof window === "undefined") return
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission()
    }
    let pulses = 0
    const fire = () => {
      pulses += 1
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("Urgent message", {
          body: content,
          tag: `urgent-${Date.now()}`,
          requireInteraction: false,
        })
      } else {
        console.log("[v0] urgent pulse", pulses, content)
      }
      if (pulses >= 10 && urgentIntervalRef.current) {
        clearInterval(urgentIntervalRef.current)
        urgentIntervalRef.current = null
        setUrgentMode(false)
      }
    }
    fire()
    urgentIntervalRef.current = setInterval(fire, 2 * 60 * 1000)
    setUrgentMode(true)
  }

  /**
   * Voice-to-text dictation toggle. Streams interim results into the
   * message input. Falls back gracefully when SpeechRecognition is
   * unsupported.
   */
  const handleVoiceToText = () => {
    const SR =
      (typeof window !== "undefined" &&
        ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)) || null
    if (!SR) {
      console.log("[v0] SpeechRecognition unavailable in this browser")
      return
    }
    if (isDictating && dictationRecognitionRef.current) {
      dictationRecognitionRef.current.stop()
      dictationRecognitionRef.current = null
      setIsDictating(false)
      return
    }
    const recog = new SR()
    recog.continuous = true
    recog.interimResults = true
    recog.lang = "en-US"
    let finalText = messageInput
    recog.onresult = (e: any) => {
      let interim = ""
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const txt = e.results[i][0].transcript
        if (e.results[i].isFinal) finalText += (finalText ? " " : "") + txt.trim()
        else interim += txt
      }
      setMessageInput((finalText + (interim ? " " + interim : "")).replace(/\s+/g, " "))
    }
    recog.onend = () => {
      setIsDictating(false)
      dictationRecognitionRef.current = null
    }
    recog.onerror = () => {
      setIsDictating(false)
      dictationRecognitionRef.current = null
    }
    recog.start()
    dictationRecognitionRef.current = recog
    setIsDictating(true)
  }

  /**
   * Generate and download an .ics file for an appointment message
   * so the recipient can import it into their calendar.
   */
  const addAppointmentToCalendar = (appt: NonNullable<Message["appointment"]>) => {
    const dayIndex: Record<string, number> = {
      Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6,
    }
    const target = dayIndex[appt.day] ?? new Date().getDay()
    const now = new Date()
    const next = new Date(now)
    const diff = (target - now.getDay() + 7) % 7 || 7
    next.setDate(now.getDate() + diff)

    const parseTime = (raw: string, base: Date) => {
      const m = raw.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i)
      if (!m) return new Date(base)
      let h = Number(m[1])
      const mins = Number(m[2])
      const ap = m[3]?.toUpperCase()
      if (ap === "PM" && h < 12) h += 12
      if (ap === "AM" && h === 12) h = 0
      const d = new Date(base)
      d.setHours(h, mins, 0, 0)
      return d
    }
    const startSrc = appt.slotStart || (appt.slotLabel.split("-")[0]?.trim() ?? "09:00")
    const endSrc   = appt.slotEnd   || (appt.slotLabel.split("-")[1]?.trim() ?? "")
    const start = parseTime(startSrc, next)
    const end   = endSrc ? parseTime(endSrc, next) : new Date(start.getTime() + 60 * 60 * 1000)

    const fmt = (d: Date) =>
      d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "")
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Edemics//Chat Popup Appointments//EN",
      "BEGIN:VEVENT",
      `UID:appt-${Date.now()}@edemics`,
      `DTSTAMP:${fmt(new Date())}`,
      `DTSTART:${fmt(start)}`,
      `DTEND:${fmt(end)}`,
      `SUMMARY:${appt.purpose} with ${appt.hostName}`,
      `DESCRIPTION:${(appt.notes || "").replace(/\n/g, "\\n")}`,
      `LOCATION:${appt.apptType === "physical" ? "On-site" : "Virtual"}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n")
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `appointment-${appt.day.toLowerCase()}.ics`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  /**
   * Reference Transaction — append `[txn:DocType:ID]` to the chat
   * draft. The renderer turns this into a clickable hyperlink chip.
   */
  const insertTransactionReference = () => {
    if (!transactionDocType || !transactionId) return
    setMessageInput(prev =>
      (prev ? prev.trimEnd() + " " : "") + `[txn:${transactionDocType}:${transactionId}]`
    )
    setShowTransactionDialog(false)
    setTransactionDocType("")
    setTransactionId("")
  }

  /**
   * Reference Document — append `[doc:DocType:ID]` to the chat
   * draft. Same hyperlink-chip rendering as transactions but a
   * different icon (Link2 vs Hash).
   */
  const insertDocumentReference = () => {
    if (!documentDocType || !documentId) return
    setMessageInput(prev =>
      (prev ? prev.trimEnd() + " " : "") + `[doc:${documentDocType}:${documentId}]`
    )
    setShowDocumentDialog(false)
    setDocumentDocType("")
    setDocumentId("")
  }

  /**
   * Custom chat background — read the chosen image file as a data
   * URL and apply it to the ScrollArea. Empty file resets.
   */
  const handleCustomBackgroundUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      setCustomChatBackground(String(ev.target?.result || ""))
      // Switch the shared preset to "custom" so the in-page chat
      // module also picks up the new background.
      setChatBackground("custom")
    }
    reader.readAsDataURL(file)
    if (e.target) e.target.value = ""
  }

  // ... (keep all existing render functions for messages, attachments, etc.)

  if (selectedChat) {
    // --- Chat Room View ---
    return (
      <div className="w-96 h-[600px] bg-light-card-bg dark:bg-dark-card-bg rounded-lg shadow-xl flex flex-col border border-divider">
        {/* Chat Room Header */}
        <div className="p-3 border-b border-divider flex items-center">
          <Button
            variant="ghost"
            size="icon"
            className="mr-2"
            onClick={() => {
              setSelectedChat(null)
              setFilesToPreview([])
              setReplyingTo(null)
              setEditingMessage(null)
              setIsSelectionMode(false)
              setSelectedMessages([])
              handleCancelRecording()
            }}
          >
            <UsersRound className="h-5 w-5 text-light-text-primary dark:text-dark-text-primary" />
          </Button>
          <Avatar className="h-9 w-9 mr-3">
            <AvatarImage src={selectedChat.avatar || "/placeholder.svg"} alt={selectedChat.name} />
            <AvatarFallback>{selectedChat.name.substring(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary">
              {selectedChat.name}
            </h3>
            {selectedChat.type === "direct" && selectedChat.isOnline && <p className="text-xs text-success">Online</p>}
            {selectedChat.type === "group" && (
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                {selectedChat.members?.length || 0} members,{" "}
                {selectedChat.members?.filter((m) => m.isOnline).length || 0} online
              </p>
            )}
            {isTyping && (
              <div className="flex items-center gap-1 text-xs text-primary">
                <span>typing</span>
                <div className="flex gap-1">
                  <div className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {/* Header Actions */}
          <div className="flex items-center gap-1">
            {/* Settings icon — shows a Chat Settings dropdown for both
                direct and group chats. Group chats get an additional
                "Group Settings" item that mirrors the dialog in
                /communication/chat. The bulk-forward checkbox that used
                to live here has been moved to the per-message ellipsis
                ("Forward") action, which now enables selection mode. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Chat settings">
                  <Settings2 className="h-5 w-5 text-light-text-primary dark:text-dark-text-primary" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>
                  <Pin className="h-4 w-4 mr-2" />
                  {selectedChat.isPinned ? "Unpin Chat" : "Pin Chat"}
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Bell className="h-4 w-4 mr-2" />
                  Mute Notifications
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsSelectionMode(true)}>
                  <Forward className="h-4 w-4 mr-2" />
                  Forward Messages
                </DropdownMenuItem>
                {selectedChat.type === "group" && (
                  <>
                    <DropdownMenuItem onClick={() => handleGroupSettings("info")}>
                      <Info className="h-4 w-4 mr-2" />
                      Group Info
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleGroupSettings("add-members")}>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Members
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowGroupSettingsDialog(true)}>
                      <Shield className="h-4 w-4 mr-2" />
                      Group Settings
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={() => setShowChatSettingsDialog(true)}>
                  <Settings className="h-4 w-4 mr-2" />
                  Chat Settings
                </DropdownMenuItem>
                {selectedChat.type === "group" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleGroupSettings("leave")} className="text-destructive">
                      <LogOut className="h-4 w-4 mr-2" />
                      Leave Group
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close chat">
              <X className="h-5 w-5 text-light-text-primary dark:text-dark-text-primary" />
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-3 border-b border-divider">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-light-text-secondary dark:text-dark-text-secondary" />
            <Input
              placeholder="Search messages..."
              className="pl-10 bg-light-bg dark:bg-dark-bg border-divider focus-visible:ring-primary"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Bulk Actions Bar — appears when selection mode is on (now
            triggered from a message ellipsis "Forward" action). Users
            can pick additional messages, then forward/delete or cancel. */}
        {isSelectionMode && (
          <div className="p-3 border-b border-divider bg-light-bg dark:bg-dark-bg flex items-center justify-between">
            <span className="text-sm text-light-text-primary dark:text-dark-text-primary">
              {selectedMessages.length} selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={selectedMessages.length === 0}
                onClick={() => {
                  selectedMessages.forEach((id) => handleForwardMessage(id))
                  setSelectedMessages([])
                  setIsSelectionMode(false)
                }}
              >
                <Forward className="h-4 w-4 mr-1" />
                Forward
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={selectedMessages.length === 0}
                onClick={handleBulkDelete}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedMessages([])
                  setIsSelectionMode(false)
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Pinned Messages bar — when one or more messages are
            pinned, show them collapsed at the top of the chat with a
            click-to-jump scroll behaviour and an inline unpin
            shortcut. Mirrors the bar in /communication/chat. */}
        {messages.some(m => m.isPinned) && (
          <div className="px-3 py-2 border-b border-divider bg-light-bg dark:bg-dark-bg">
            <div className="flex items-center gap-1.5 mb-1 text-[10px] font-semibold uppercase tracking-wide text-primary">
              <Pin className="h-3 w-3" />
              <span>Pinned</span>
            </div>
            <div className="space-y-1">
              {messages.filter(m => m.isPinned).map(pm => (
                <div
                  key={`pin-${pm.id}`}
                  className="flex items-start gap-2 px-2 py-1 rounded-md hover:bg-light-card-bg dark:hover:bg-dark-card-bg cursor-pointer group/pin"
                  onClick={() => {
                    const el = document.getElementById(`chat-msg-${pm.id}`)
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth", block: "center" })
                      el.classList.add("ring-2", "ring-primary", "ring-offset-2")
                      setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-offset-2"), 1600)
                    }
                  }}
                >
                  <Pin className="h-3 w-3 mt-0.5 text-primary flex-shrink-0" />
                  <p className="flex-1 text-[11px] leading-tight text-light-text-primary dark:text-dark-text-primary line-clamp-1">
                    {pm.content || "Attachment"}
                  </p>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 opacity-0 group-hover/pin:opacity-100 transition-opacity"
                    onClick={e => { e.stopPropagation(); unpinMessage(pm.id) }}
                    aria-label="Unpin message"
                  >
                    <PinOff className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages Area — honours the SAME background preset as
            /communication/chat (lifted into ChatContext) so picking a
            background in either surface mirrors here. When the user
            uploads a custom image, it is rendered on its own
            absolutely-positioned layer so the opacity slider only
            affects the photo, never the message text. */}
        <div
          className={cn(
            "relative flex-1 min-h-0",
            chatBackground === "default" && "bg-[#F4F5FA] dark:bg-[#28243D]",
            chatBackground === "gradient1" &&
              "bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20",
            chatBackground === "gradient2" &&
              "bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20",
            chatBackground === "pattern" &&
              "bg-[#F4F5FA] dark:bg-[#28243D] bg-opacity-50",
            chatBackground === "custom" && "bg-[#F4F5FA] dark:bg-[#28243D]",
          )}
        >
          {chatBackground === "custom" && customChatBackground && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage: `url(${customChatBackground})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: customChatBackgroundOpacity,
              }}
            />
          )}
          <ScrollArea className="relative h-full px-3 py-4">
          <div className="space-y-3">
            {messages
              .filter((msg) => {
                if (!searchQuery) return true
                return msg.content?.toLowerCase().includes(searchQuery.toLowerCase())
              })
              .filter((msg) => {
                if (selectedChat.type === "direct" && (msg.sender === selectedChat.name || msg.sender === "me"))
                  return true
                return true
              })
              .map((msg) => {
                const isMe = msg.sender === "me"
                const displayName = isMe ? "You" : msg.senderName || msg.sender
                const initials = (displayName || "?").substring(0, 2).toUpperCase()
                const hasQuote = !!msg.replyTo
                // Custom-image background flag — only true when the
                // user has uploaded a photo. Preset backgrounds keep
                // the standard tinted bubbles.
                const hasCustomBg =
                  chatBackground === "custom" && !!customChatBackground
                // Resolve the original message by id (robust) so we can reliably
                // tell whether the quoted message was authored by me or by them.
                // Falls back to replyTo.sender string comparison if not found.
                const quotedOriginal = hasQuote
                  ? messages.find((m) => m.id === msg.replyTo!.messageId)
                  : undefined
                const quoteFromMe =
                  hasQuote &&
                  (quotedOriginal
                    ? quotedOriginal.sender === "me"
                    : msg.replyTo!.sender === "me" || msg.replyTo!.sender === "You")

                const scrollToQuoted = () => {
                  if (!msg.replyTo?.messageId) return
                  const target = document.getElementById(`chat-msg-${msg.replyTo.messageId}`)
                  if (target) {
                    target.scrollIntoView({ behavior: "smooth", block: "center" })
                    // brief highlight flash
                    target.classList.add("ring-2", "ring-primary", "ring-offset-2")
                    window.setTimeout(() => {
                      target.classList.remove("ring-2", "ring-primary", "ring-offset-2")
                    }, 1600)
                  }
                }

                return (
                  <div
                    key={msg.id}
                    id={`chat-msg-${msg.id}`}
                    className={cn(
                      "group relative flex gap-2 transition-shadow rounded-xl",
                      isMe ? "flex-row-reverse" : "flex-row",
                    )}
                  >
                    {/* Selection checkbox */}
                    {isSelectionMode && (
                      <Checkbox
                        checked={selectedMessages.includes(msg.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedMessages((prev) => [...prev, msg.id])
                          } else {
                            setSelectedMessages((prev) => prev.filter((id) => id !== msg.id))
                          }
                        }}
                        className="mt-2 flex-shrink-0"
                      />
                    )}

                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <Avatar
                        className={cn(
                          "w-7 h-7 ring-2",
                          isMe ? "ring-primary/40" : "ring-primary/20",
                        )}
                      >
                        <AvatarImage src={msg.avatar || "/placeholder.svg"} alt={displayName} />
                        <AvatarFallback
                          className={cn(
                            "text-[10px] font-semibold",
                            isMe ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary",
                          )}
                        >
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                    </div>

                    {/* Content column — scaled down, capped at 78% width */}
                    <div
                      className={cn(
                        "flex flex-col min-w-0 max-w-[78%]",
                        isMe ? "items-end" : "items-start",
                      )}
                    >
                      {/* Header: name + role badge + timestamp (order flips for me) */}
                      <div
                        className={cn(
                          "flex items-center gap-1.5 mb-1 flex-wrap",
                          isMe ? "flex-row-reverse" : "flex-row",
                        )}
                      >
                        <span className="text-xs font-semibold text-light-text-primary dark:text-dark-text-primary">
                          {displayName}
                        </span>
                        <span className="text-[10px] text-light-text-secondary dark:text-dark-text-secondary whitespace-nowrap">
                          {msg.timestamp}
                          {msg.isEdited && <span className="italic ml-1">(edited)</span>}
                        </span>
                      </div>

                      {/*
                       * Bubble container — fill rules mirror
                       * /communication/chat exactly:
                       *   • Custom uploaded image  → mine: secondary,
                       *                              theirs: white
                       *   • All other backgrounds  → mine: primary tint,
                       *                              theirs: info @16%
                       * Text colour is held constant.
                       */}
                      <div
                        className={cn(
                          "relative px-3 py-2 rounded-xl border break-words w-full text-light-text-primary dark:text-dark-text-primary",
                          isMe
                            ? cn(
                                "rounded-tr-none",
                                hasCustomBg
                                  ? "bg-secondary border-secondary"
                                  : "bg-primary/10 dark:bg-primary/20 border-primary/30",
                              )
                            : cn(
                                "rounded-tl-none",
                                hasCustomBg
                                  ? "bg-white dark:bg-[#312D4B] border-white dark:border-[#312D4B]"
                                  : "bg-info/[0.16] border-info/30",
                              ),
                        )}
                      >
                        {/* Speech-bubble tail — colours track the
                            bubble fill rules above. */}
                        {isMe ? (
                          <>
                            <span
                              aria-hidden
                              className={cn(
                                "absolute top-0 -right-[9px] w-[9px] h-[9px]",
                                hasCustomBg ? "bg-secondary" : "bg-primary/30",
                              )}
                              style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }}
                            />
                            <span
                              aria-hidden
                              className={cn(
                                "absolute top-0 -right-[8px] w-[8px] h-[8px]",
                                hasCustomBg
                                  ? "bg-secondary"
                                  : "bg-primary/10 dark:bg-primary/20",
                              )}
                              style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }}
                            />
                          </>
                        ) : (
                          <>
                            <span
                              aria-hidden
                              className={cn(
                                "absolute top-0 -left-[9px] w-[9px] h-[9px]",
                                hasCustomBg ? "bg-white dark:bg-[#312D4B]" : "bg-info/30",
                              )}
                              style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%)" }}
                            />
                            <span
                              aria-hidden
                              className={cn(
                                "absolute top-0 -left-[8px] w-[8px] h-[8px]",
                                hasCustomBg ? "bg-white dark:bg-[#312D4B]" : "bg-info/[0.16]",
                              )}
                              style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%)" }}
                            />
                          </>
                        )}
                        {/* Quoted reply-to card — clickable, scrolls to original message */}
                        {hasQuote && (
                          <button
                            type="button"
                            onClick={scrollToQuoted}
                            className={cn(
                              "w-full text-left mb-2 pl-2 pr-2 py-1.5 rounded-md border-l-4 transition-colors",
                              "hover:brightness-95 dark:hover:brightness-110",
                              quoteFromMe
                                ? "bg-primary/[0.16] border-primary"
                                : "bg-[#26C6F9]/[0.16] border-[#26C6F9]",
                            )}
                            aria-label="Jump to quoted message"
                          >
                            <p
                              className={cn(
                                "text-[10px] font-semibold mb-0.5",
                                quoteFromMe ? "text-primary" : "text-[#0284c7]",
                              )}
                            >
                              {quoteFromMe ? "You" : msg.replyTo!.sender}
                            </p>
                            <p className="text-[11px] leading-tight text-light-text-primary dark:text-dark-text-primary line-clamp-2">
                              {msg.replyTo!.content || "Attachment"}
                            </p>
                          </button>
                        )}

                        {/* Scheduled-message indicator — sits at the
                            top of the bubble so the user knows the
                            message will be delivered later. */}
                        {msg.scheduledFor && (
                          <div className="mb-1.5 flex items-center gap-1 text-[10px] font-medium text-primary">
                            <Clock className="h-3 w-3" />
                            <span>
                              Scheduled for{" "}
                              {msg.scheduledFor.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                              {" • "}
                              {msg.scheduledFor.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                        )}

                        {/* Urgent banner. */}
                        {msg.isUrgent && (
                          <div className="mb-1.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-red-500">
                            <AlertTriangle className="h-3 w-3" />
                            Urgent
                          </div>
                        )}

                        {/* Inline appointment confirmation card. */}
                        {msg.appointment && (
                          <div className="rounded-lg border border-primary/30 bg-light-card-bg dark:bg-dark-card-bg p-2.5 mb-2">
                            <div className="flex items-center gap-1.5 mb-1.5 text-primary">
                              <Calendar className="h-3.5 w-3.5" />
                              <span className="text-[10px] font-bold uppercase tracking-wide">
                                Appointment
                              </span>
                            </div>
                            <p className="text-[12px] font-semibold text-light-text-primary dark:text-dark-text-primary">
                              {msg.appointment.purpose}
                            </p>
                            <p className="text-[11px] text-light-text-secondary dark:text-dark-text-secondary">
                              With: {msg.appointment.hostName}
                            </p>
                            <div className="flex items-center gap-2 pt-1 text-[11px] text-light-text-primary dark:text-dark-text-primary">
                              <span className="inline-flex items-center gap-1">
                                <Calendar className="h-3 w-3 text-primary" />
                                {msg.appointment.day}
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <Clock className="h-3 w-3 text-primary" />
                                {msg.appointment.slotLabel || "Time TBD"}
                              </span>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="mt-2 w-full h-7 text-[11px] gap-1 border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                              onClick={() => addAppointmentToCalendar(msg.appointment!)}
                            >
                              <Calendar className="h-3 w-3" />
                              Add to Calendar
                            </Button>
                          </div>
                        )}

                        {/* Inline document-reference chip. */}
                        {msg.referencedDocument && (
                          <a
                            href="#"
                            onClick={e => e.preventDefault()}
                            className="inline-flex items-center gap-1.5 px-2 py-1 mb-1.5 rounded-md border border-primary/30 bg-primary/10 text-[11px] text-primary hover:bg-primary/20"
                          >
                            <Link2 className="h-3 w-3" />
                            <span className="font-medium">{msg.referencedDocument.docType}</span>
                            <span>•</span>
                            <span>{msg.referencedDocument.id}</span>
                          </a>
                        )}

                        {/* Message body — inline edit OR parsed text. */}
                        {editingMessage === msg.id ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editContent}
                              onChange={e => setEditContent(e.target.value)}
                              rows={2}
                              className="text-[13px] resize-none"
                              autoFocus
                            />
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-[11px]"
                                onClick={() => { setEditingMessage(null); setEditContent("") }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                className="h-7 text-[11px]"
                                onClick={() => handleEditSave(msg.id)}
                              >
                                Save
                              </Button>
                            </div>
                          </div>
                        ) : (
                          msg.content && (
                            <p className="text-[13px] leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-light-text-primary dark:text-dark-text-primary">
                              {parseMessageContent(msg.content)}
                            </p>
                          )
                        )}
                        {msg.attachments && msg.attachments.map(renderAttachment)}
                        {msg.voiceNote && <VoiceNotePlayer voiceNote={msg.voiceNote} isSender={isMe} />}
                        {msg.poll && renderPoll(msg.poll)}
                        {msg.task && renderTask(msg.task)}
                        {msg.event && renderEvent(msg.event)}
                      </div>

                      {/* Read-receipt ticks — outside + below the bubble, success color for "read" */}
                      {isMe && msg.status && (
                        <div className="mt-1 flex items-center gap-1 justify-end text-[10px] text-light-text-secondary dark:text-dark-text-secondary">
                          {renderMessageStatus(msg.status)}
                        </div>
                      )}

                      {/* Reactions */}
                      {renderReactions(msg.reactions)}

                      {/* Action row: Reply / React / More — order flips for me */}
                      {!isSelectionMode && (
                        <div
                          className={cn(
                            "flex items-center gap-0.5 mt-1 opacity-60 group-hover:opacity-100 transition-opacity",
                            isMe ? "flex-row-reverse" : "flex-row",
                          )}
                        >
                          {/* When a custom photo background is in use,
                              the ghost styling disappears against the
                              imagery — flip the trio to a solid primary
                              pill with white text/icon. */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className={cn(
                              "h-6 px-1.5 text-[10px] transition-colors",
                              hasCustomBg
                                ? "bg-primary text-white hover:bg-primary/90 hover:text-white"
                                : "text-light-text-secondary dark:text-dark-text-secondary hover:text-primary",
                            )}
                            onClick={() => setReplyingTo(msg)}
                          >
                            <Reply className="h-3 w-3 mr-1" />
                            Reply
                          </Button>

                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={cn(
                                  "h-6 px-1.5 text-[10px] transition-colors",
                                  hasCustomBg
                                    ? "bg-primary text-white hover:bg-primary/90 hover:text-white"
                                    : "text-light-text-secondary dark:text-dark-text-secondary hover:text-primary",
                                )}
                              >
                                <Heart className="h-3 w-3 mr-1" />
                                React
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-2">
                              <div className="flex gap-1">
                                {["❤️", "👍", "😂", "😮", "😢", "😡"].map((emoji) => (
                                  <Button
                                    key={emoji}
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => handleReaction(msg.id, emoji)}
                                  >
                                    {emoji}
                                  </Button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>

                          <div className="relative">
                            <Button
                              variant="ghost"
                              size="icon"
                              className={cn(
                                "h-6 w-6 transition-colors",
                                hasCustomBg
                                  ? "bg-primary text-white hover:bg-primary/90 hover:text-white"
                                  : "text-light-text-secondary dark:text-dark-text-secondary hover:text-primary",
                              )}
                              onClick={(e) => {
                                e.stopPropagation()
                                const menu = document.getElementById(`msg-menu-${msg.id}`)
                                if (menu) {
                                  menu.style.display = menu.style.display === "none" ? "block" : "none"
                                }
                              }}
                            >
                              <MoreVertical className="h-3 w-3" />
                            </Button>
                            <div
                              id={`msg-menu-${msg.id}`}
                              className={cn(
                                "absolute top-full mt-1 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50",
                                isMe ? "left-0" : "right-0",
                              )}
                              style={{ display: "none" }}
                            >
                              <div className="py-1" role="menu" aria-orientation="vertical">
                                <button
                                  className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCopyMessage(msg.content || "")
                                    document.getElementById(`msg-menu-${msg.id}`)!.style.display = "none"
                                  }}
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  Copy
                                </button>
                                {/* Forward — entering selection mode now
                                    happens here (the bulk-forward
                                    checkbox in the header has been
                                    removed). The clicked message is
                                    pre-selected so users can pick
                                    additional messages or hit Forward
                                    immediately from the bulk action bar. */}
                                <button
                                  className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setIsSelectionMode(true)
                                    setSelectedMessages((prev) =>
                                      prev.includes(msg.id) ? prev : [...prev, msg.id],
                                    )
                                    document.getElementById(`msg-menu-${msg.id}`)!.style.display = "none"
                                  }}
                                >
                                  <Forward className="h-4 w-4 mr-2" />
                                  Forward
                                </button>
                                {msg.sender === "me" && (
                                  <>
                                    <button
                                      className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setEditingMessage(msg.id)
                                        setEditContent(msg.content || "")
                                        document.getElementById(`msg-menu-${msg.id}`)!.style.display = "none"
                                      }}
                                    >
                                      <Edit3 className="h-4 w-4 mr-2" />
                                      Edit
                                    </button>
                                    <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                                    <button
                                      className="flex w-full items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDeleteMessage(msg.id)
                                        document.getElementById(`msg-menu-${msg.id}`)!.style.display = "none"
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </button>
                                  </>
                                )}
                                {/* Pin / Unpin — same UX as
                                    /communication/chat. Pin opens a
                                    duration dialog (30/60/90 days),
                                    Unpin clears the pin immediately. */}
                                {msg.isPinned ? (
                                  <button
                                    className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      unpinMessage(msg.id)
                                      document.getElementById(`msg-menu-${msg.id}`)!.style.display = "none"
                                    }}
                                  >
                                    <PinOff className="h-4 w-4 mr-2" />
                                    Unpin
                                  </button>
                                ) : (
                                  <button
                                    className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      openPinDialog(msg.id)
                                      document.getElementById(`msg-menu-${msg.id}`)!.style.display = "none"
                                    }}
                                  >
                                    <Pin className="h-4 w-4 mr-2" />
                                    Pin Message
                                  </button>
                                )}
                                <button
                                  className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleReadAloud(msg.content || "")
                                    document.getElementById(`msg-menu-${msg.id}`)!.style.display = "none"
                                  }}
                                >
                                  <Volume2 className="h-4 w-4 mr-2" />
                                  Read Aloud
                                </button>
                                <button
                                  className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    const menu = document.getElementById(`msg-menu-${msg.id}`)
                                    if (menu) menu.style.display = "none"
                                  }}
                                >
                                  View Delivery Report
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
          </div>
        </ScrollArea>
        </div>

        {/* Reply Preview */}
        {replyingTo && (
          <div className="p-3 border-t border-divider bg-light-bg dark:bg-dark-bg flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Reply className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-light-text-primary dark:text-dark-text-primary">
                  Replying to {replyingTo.sender === "me" ? "yourself" : replyingTo.sender}
                </p>
                <p className="text-xs text-muted-foreground truncate">{replyingTo.content || "Voice note"}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setReplyingTo(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* File Preview Area */}
        {filesToPreview.length > 0 && (
          <div className="p-3 border-t border-divider bg-light-bg dark:bg-dark-bg">
            <p className="text-xs font-medium mb-2 text-light-text-secondary dark:text-dark-text-secondary">
              Files to send ({filesToPreview.length}):
            </p>
            <ScrollArea className="max-h-32">
              <div className="space-y-2">
                {filesToPreview.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-2 p-2 rounded-md border border-divider bg-light-card-bg dark:bg-dark-card-bg"
                  >
                    {file.type === "image" && file.previewUrl ? (
                      <img src={file.previewUrl || "/placeholder.svg"}
                        alt={file.name}
                        width={32}
                        height={32}
                        className="rounded object-cover h-8 w-8"
                      />
                    ) : (
                      getFileIcon(file.name)
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate text-light-text-primary dark:text-dark-text-primary">
                        {file.name}
                      </p>
                      <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                        {file.size ? formatFileSize(file.size) : ""}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleRemoveFilePreview(file.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Recording/Preview UI */}
        {isRecording && (
          <div className="p-3 border-t border-divider bg-light-bg dark:bg-dark-bg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-destructive"></span>
              </span>
              <span className="text-sm font-medium text-destructive">
                Recording: {Math.floor(recordingDuration / 60)}:{String(recordingDuration % 60).padStart(2, "0")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={handleCancelRecording} aria-label="Cancel recording">
                <X className="h-5 w-5 text-light-text-secondary dark:text-dark-text-secondary" />
              </Button>
              <Button variant="destructive" size="icon" onClick={handleStopRecording} aria-label="Stop recording">
                <Mic className="h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {recordedAudioUrl && !isRecording && (
          <div className="p-3 border-t border-divider bg-light-bg dark:bg-dark-bg flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <audio ref={audioPreviewPlayerRef} src={recordedAudioUrl} className="hidden" preload="auto" />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => audioPreviewPlayerRef.current?.play()}
                aria-label="Play recorded audio"
              >
                <Play className="h-5 w-5 text-light-text-secondary dark:text-dark-text-secondary" />
              </Button>
              <div className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary truncate">
                Voice Note ({Math.floor(recordingDuration / 60)}:{String(recordingDuration % 60).padStart(2, "0")})
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="destructive"
                size="icon"
                onClick={handleDeleteRecordedAudio}
                aria-label="Delete recorded audio"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Message Input Area - Updated */}
        <div className="p-3 border-t border-divider bg-light-bg dark:bg-dark-bg">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            multiple
            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,audio/*,video/*,.zip,.rar"
          />

          <div className="flex items-center gap-1">
            {/* Updated Ellipses Menu with all options */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                aria-label="More options"
                onClick={(e) => {
                  e.stopPropagation()
                  const menu = document.getElementById("input-options-menu")
                  if (menu) {
                    menu.style.display = menu.style.display === "none" ? "block" : "none"
                  }
                }}
              >
                <MoreVertical className="h-5 w-5 text-light-text-secondary dark:text-dark-text-secondary" />
              </Button>
              <div
                id="input-options-menu"
                className="absolute left-0 bottom-full mb-1 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50"
                style={{ display: "none" }}
              >
                <div className="py-1" role="menu" aria-orientation="vertical">
                  <button
                    className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      const input = document.createElement("input")
                      input.type = "file"
                      input.accept = "image/*"
                      input.multiple = true
                      input.onchange = (e) => {
                        const files = (e.target as HTMLInputElement).files
                        if (files) {
                          const newFilePreviews: Attachment[] = Array.from(files).map((file, index) => ({
                            id: `preview-${Date.now()}-${index}`,
                            name: file.name,
                            type: "image",
                            url: "#",
                            previewUrl: URL.createObjectURL(file),
                            size: file.size,
                          }))
                          setFilesToPreview((prev) => [...prev, ...newFilePreviews])
                        }
                      }
                      input.click()
                      document.getElementById("input-options-menu")!.style.display = "none"
                    }}
                  >
                    <FileImage className="h-4 w-4 mr-2" />
                    Attach Image
                  </button>
                  <button
                    className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (fileInputRef.current) {
                        fileInputRef.current.click()
                      }
                      document.getElementById("input-options-menu")!.style.display = "none"
                    }}
                  >
                    <Paperclip className="h-4 w-4 mr-2" />
                    Attach File (Docs, PDF, etc.)
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                  <button
                    className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowPollDialog(true)
                      document.getElementById("input-options-menu")!.style.display = "none"
                    }}
                  >
                    <ThumbsUp className="h-4 w-4 mr-2" />
                    Create Poll
                  </button>
                  <button
                    className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowEventDialog(true)
                      document.getElementById("input-options-menu")!.style.display = "none"
                    }}
                  >
                    <CalendarPlus className="h-4 w-4 mr-2" />
                    Create Event
                  </button>
                  <button
                    className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowTaskDialog(true)
                      document.getElementById("input-options-menu")!.style.display = "none"
                    }}
                  >
                    <ListChecks className="h-4 w-4 mr-2" />
                    Create Task
                  </button>
                  {/* ── Parity additions: schedule / references / dictation / urgent / bg ─ */}
                  <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                  <button
                    className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      setScheduleDraft(messageInput)
                      setShowScheduleDialog(true)
                      document.getElementById("input-options-menu")!.style.display = "none"
                    }}
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Schedule Message
                  </button>
                  <button
                    className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowTransactionDialog(true)
                      document.getElementById("input-options-menu")!.style.display = "none"
                    }}
                  >
                    <Hash className="h-4 w-4 mr-2" />
                    Reference Transaction
                  </button>
                  <button
                    className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowDocumentDialog(true)
                      document.getElementById("input-options-menu")!.style.display = "none"
                    }}
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Reference Document
                  </button>
                  {/* Voice Note — moved here from the input toolbar.
                      The mic button next to the message field now
                      triggers Voice-to-Text dictation instead. */}
                  <button
                    className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleStartRecording()
                      document.getElementById("input-options-menu")!.style.display = "none"
                    }}
                  >
                    <Mic className="h-4 w-4 mr-2" />
                    Send Voice Note
                  </button>
                  <button
                    className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (!messageInput.trim()) {
                        console.log("[v0] Type a message first to mark it urgent")
                      } else {
                        triggerUrgentNotifications(messageInput.trim())
                      }
                      document.getElementById("input-options-menu")!.style.display = "none"
                    }}
                  >
                    <AlertTriangle className="h-4 w-4 mr-2 text-red-500" />
                    {urgentMode ? "Urgent Active" : "Mark Urgent"}
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                  <label
                    className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {customChatBackground ? "Change Background" : "Upload Chat Background"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        handleCustomBackgroundUpload(e)
                        document.getElementById("input-options-menu")!.style.display = "none"
                      }}
                    />
                  </label>
                  {customChatBackground && (
                    <>
                      {/* Background opacity slider — only meaningful for
                          uploaded photos. Click handlers stop propagation
                          so dragging the slider doesn't dismiss the menu. */}
                      <div
                        className="px-4 py-3 border-t border-divider"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-light-text-primary dark:text-dark-text-primary">
                            Background Opacity
                          </span>
                          <span className="text-xs tabular-nums text-light-text-secondary dark:text-dark-text-secondary">
                            {Math.round(customChatBackgroundOpacity * 100)}%
                          </span>
                        </div>
                        <input
                          aria-label="Custom background opacity"
                          type="range"
                          min={0.1}
                          max={1}
                          step={0.05}
                          value={customChatBackgroundOpacity}
                          onChange={(e) =>
                            setCustomChatBackgroundOpacity(Number(e.target.value))
                          }
                          className="w-full h-1.5 rounded-full appearance-none bg-divider accent-primary cursor-pointer"
                        />
                      </div>
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={(e) => {
                          e.stopPropagation()
                          setCustomChatBackground(null)
                          // Reset the shared preset back to "default" so
                          // the in-page chat module also clears.
                          setChatBackground("default")
                          document.getElementById("input-options-menu")!.style.display = "none"
                        }}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Remove Background
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 relative min-w-0">
              <Input
                ref={messageInputRef}
                type="text"
                placeholder={isRecording ? "Recording..." : recordedAudioUrl ? "Voice note ready" : "Type a message..."}
                className="bg-light-card-bg dark:bg-dark-card-bg border-divider focus-visible:ring-primary pr-10 w-full"
                value={messageInput}
                onChange={handleInputChange}
                onKeyPress={(e) => e.key === "Enter" && !isRecording && handleSendMessage()}
                disabled={isRecording || !!recordedAudioUrl}
              />

              {/* @ Mentions Dropdown - Fixed positioning */}
              {showMentions && selectedChat?.type === "group" && filteredMembers.length > 0 && (
                <div className="absolute bottom-full left-0 right-0 mb-1 bg-background border border-divider rounded-md shadow-lg max-h-32 overflow-y-auto z-50">
                  {filteredMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-2 p-2 hover:bg-muted cursor-pointer"
                      onClick={() => handleMentionSelect(member)}
                    >
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={member.avatar || "/placeholder.svg"} />
                        <AvatarFallback>{member.name.substring(0, 1)}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{member.name}</span>
                      {member.isOnline && <span className="w-2 h-2 bg-green-500 rounded-full"></span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Voice recording and send buttons */}
            {/* Voice-to-Text dictation button (replaces the previous
                voice-note shortcut here — voice notes are now in the
                input ellipsis menu as "Send Voice Note"). */}
            {!isRecording && !recordedAudioUrl && messageInput.trim() === "" && filesToPreview.length === 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleVoiceToText}
                      aria-label={isDictating ? "Stop dictation" : "Voice to text"}
                    >
                      <Mic
                        className={cn(
                          "h-5 w-5",
                          isDictating
                            ? "text-red-500 animate-pulse"
                            : "text-light-text-secondary dark:text-dark-text-secondary",
                        )}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{isDictating ? "Stop dictation" : "Voice to text"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {!isRecording && !recordedAudioUrl && (messageInput.trim() !== "" || filesToPreview.length > 0) && (
              <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Add emoji">
                    <Smile className="h-5 w-5 text-light-text-secondary dark:text-dark-text-secondary" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2">
                  <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
                    {emojis.map((emoji, index) => (
                      <Button
                        key={index}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          setMessageInput((prev) => prev + emoji)
                          setShowEmojiPicker(false)
                        }}
                      >
                        {emoji}
                      </Button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {!isRecording && (messageInput.trim() !== "" || filesToPreview.length > 0 || recordedAudioUrl) && (
              <Button
                size="icon"
                onClick={handleSendMessage}
                className="bg-primary hover:bg-primary/90"
                aria-label="Send message"
              >
                <Send className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>

        {/* ── Parity dialogs imported from /communication/chat ─────────
            Schedule Message, Reference Transaction, Reference Document,
            and Pin Message duration. They share styling/spacing with
            the existing Poll/Task/Event dialogs below. */}

        {/* Schedule Message Dialog */}
        <Dialog
          open={showScheduleDialog}
          onOpenChange={(o) => {
            setShowScheduleDialog(o)
            if (!o) { setScheduleDraft(""); setScheduleDateTime("") }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Send Scheduled Message</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label htmlFor="schedule-when" className="text-xs">Send At</Label>
                <Input
                  id="schedule-when"
                  type="datetime-local"
                  value={scheduleDateTime}
                  onChange={(e) => setScheduleDateTime(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="schedule-msg" className="text-xs">Message</Label>
                <Textarea
                  id="schedule-msg"
                  value={scheduleDraft}
                  onChange={(e) => setScheduleDraft(e.target.value)}
                  placeholder="Type the message you want to schedule..."
                  rows={4}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
                <Button
                  disabled={!scheduleDateTime || !scheduleDraft.trim()}
                  onClick={scheduleMessage}
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Schedule
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reference Transaction Dialog — cascading DocType -> Tx ID */}
        <Dialog
          open={showTransactionDialog}
          onOpenChange={(o) => {
            setShowTransactionDialog(o)
            if (!o) { setTransactionDocType(""); setTransactionId("") }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reference Transaction</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {/* DocType — linked field (searchable) listing every
                  doctype configured in the software. */}
              <StandardInputField
                label="DocType"
                type="link"
                value={transactionDocType}
                onChange={(v) => { setTransactionDocType(v as string); setTransactionId("") }}
                placeholder="Search or select a doctype..."
                options={DOCTYPES.map(dt => ({ value: dt, label: dt }))}
                searchable
                required
              />
              {/* Transaction ID — linked field (searchable) listing
                  every transaction created within the chosen doctype. */}
              <StandardInputField
                label="Transaction ID"
                type="link"
                value={transactionId}
                onChange={(v) => setTransactionId(v as string)}
                placeholder={transactionDocType ? "Search or select a transaction..." : "Select a doctype first"}
                options={(TRANSACTIONS_BY_DOCTYPE[transactionDocType] ?? []).map(t => ({
                  value: t.id,
                  label: `${t.id} — ${t.label}`,
                  subtitle: t.label,
                }))}
                searchable
                readOnly={!transactionDocType}
                disabled={!transactionDocType}
                icon={<Hash className="h-4 w-4" />}
                required
              />
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                The selected transaction will be inserted into your message field as a reference. Add any additional notes before sending.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowTransactionDialog(false)}>Cancel</Button>
                <Button
                  disabled={!transactionDocType || !transactionId}
                  onClick={insertTransactionReference}
                >
                  <Hash className="h-4 w-4 mr-2" />
                  Insert Reference
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reference Document Dialog */}
        <Dialog
          open={showDocumentDialog}
          onOpenChange={(o) => {
            setShowDocumentDialog(o)
            if (!o) { setDocumentDocType(""); setDocumentId("") }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reference Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <StandardInputField
                label="DocType"
                type="link"
                value={documentDocType}
                onChange={(v) => { setDocumentDocType(v as string); setDocumentId("") }}
                placeholder="Search or select a doctype..."
                options={DOCTYPES.map(dt => ({ value: dt, label: dt }))}
                searchable
                required
              />
              <StandardInputField
                label="Document ID"
                type="link"
                value={documentId}
                onChange={(v) => setDocumentId(v as string)}
                placeholder={documentDocType ? "Search or select a document..." : "Select a doctype first"}
                options={(TRANSACTIONS_BY_DOCTYPE[documentDocType] ?? []).map(t => ({
                  value: t.id,
                  label: `${t.id} — ${t.label}`,
                  subtitle: t.label,
                }))}
                searchable
                readOnly={!documentDocType}
                disabled={!documentDocType}
                icon={<Link2 className="h-4 w-4" />}
                required
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDocumentDialog(false)}>Cancel</Button>
                <Button
                  disabled={!documentDocType || !documentId}
                  onClick={insertDocumentReference}
                >
                  <Link2 className="h-4 w-4 mr-2" />
                  Insert Reference
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Pin Message Dialog — choose 30/60/90-day expiry. */}
        <Dialog open={showPinDialog} onOpenChange={setShowPinDialog}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Pin Message</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Pin this message to the top of the chat. It will automatically unpin after the chosen duration.
              </p>
              <div className="space-y-2">
                {([
                  { value: "30", label: "30 days" },
                  { value: "60", label: "60 days" },
                  { value: "90", label: "90 days" },
                ] as const).map(opt => (
                  <label
                    key={opt.value}
                    className={cn(
                      "flex items-center gap-3 p-2.5 rounded-md border cursor-pointer transition-colors",
                      pinDuration === opt.value
                        ? "border-primary bg-primary/10"
                        : "border-divider hover:bg-light-bg dark:hover:bg-dark-bg",
                    )}
                  >
                    <input
                      type="radio"
                      name="pin-duration"
                      value={opt.value}
                      checked={pinDuration === opt.value}
                      onChange={() => setPinDuration(opt.value)}
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowPinDialog(false)}>Cancel</Button>
                <Button onClick={confirmPinMessage}>
                  <Pin className="h-4 w-4 mr-2" />
                  Pin
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* All Dialog Components - Poll, Task, Event, Group Settings */}
        <Dialog open={showPollDialog} onOpenChange={setShowPollDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Poll</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <StandardInputField
                label="Question"
                value={pollForm.question}
                onChange={(v) => setPollForm((prev) => ({ ...prev, question: v }))}
                placeholder="What's your question?"
                required
              />
              <div>
                <Label className="text-sm font-medium mb-2 block">Type</Label>
                <div className="flex flex-wrap gap-2">
                  {(["single", "multiple", "yesno", "custom"] as const).map((t) => (
                    <Button
                      key={t}
                      size="sm"
                      variant={pollForm.type === t ? "default" : "outline"}
                      onClick={() => setPollForm((prev) => ({ ...prev, type: t }))}
                      className={pollForm.type === t ? "bg-primary text-white" : "bg-transparent"}
                    >
                      {t === "single" ? "Single Choice" : t === "multiple" ? "Multiple Choice" : t === "yesno" ? "Yes/No" : "Custom"}
                    </Button>
                  ))}
                </div>
              </div>
              {pollForm.type === "custom" && (
                <div className="space-y-2">
                  {pollForm.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...pollForm.options]
                          newOptions[index] = e.target.value
                          setPollForm((prev) => ({ ...prev, options: newOptions }))
                        }}
                        placeholder={`Option ${index + 1}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const newOptions = [...pollForm.options]
                          newOptions.splice(index, 1)
                          setPollForm((prev) => ({ ...prev, options: newOptions }))
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPollForm((prev) => ({ ...prev, options: [...prev.options, ""] }))}
                  >
                    Add Option
                  </Button>
                </div>
              )}
              <div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="allow-custom-options"
                    checked={pollForm.allowCustomOptions}
                    onCheckedChange={(checked) =>
                      setPollForm((prev) => ({ ...prev, allowCustomOptions: checked || false }))
                    }
                  />
                  <Label htmlFor="allow-custom-options">Allow voters to add options</Label>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreatePoll} className="flex-1">
                  Create Poll
                </Button>
                <Button variant="outline" onClick={() => setShowPollDialog(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Task Dialog — mirrors the dialog used by
            /academics/events-calendar (parent-calendar) so that tasks
            created from chat have the same fields and behaviour as
            tasks created from the calendar. */}
        <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">Create Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Task Title */}
              <StandardInputField
                label="Task Title"
                value={taskForm.title}
                onChange={(v) => setTaskForm((prev) => ({ ...prev, title: v as string }))}
                type="text"
                placeholder="Enter task title"
                required
              />
              {/* Start Date and Due Date */}
              <div className="grid grid-cols-2 gap-4">
                <StandardInputField
                  label="Start Date"
                  value={taskForm.startDate}
                  onChange={(v) => setTaskForm((prev) => ({ ...prev, startDate: v as string }))}
                  type="date"
                 
                />
                <StandardInputField
                  label="Due Date"
                  value={taskForm.dueDate}
                  onChange={(v) => setTaskForm((prev) => ({ ...prev, dueDate: v as string }))}
                  type="date"
                 
                  required
                />
              </div>
              {/* Status and Priority */}
              <div className="grid grid-cols-2 gap-4">
                <StandardInputField
                  label="Status"
                  value={taskForm.status}
                  onChange={(v) =>
                    setTaskForm((prev) => ({
                      ...prev,
                      status: v as "not-started" | "in-progress" | "completed",
                    }))
                  }
                  type="select"
                  options={[
                    { value: "not-started", label: "Not Started" },
                    { value: "in-progress", label: "In Progress" },
                    { value: "completed", label: "Completed" },
                  ]}
                  placeholder="Select status"
                />
                <StandardInputField
                  label="Priority"
                  value={taskForm.priority}
                  onChange={(v) =>
                    setTaskForm((prev) => ({ ...prev, priority: v as "low" | "normal" | "high" }))
                  }
                  type="select"
                  options={[
                    { value: "low", label: "Low" },
                    { value: "normal", label: "Normal" },
                    { value: "high", label: "High" },
                  ]}
                  placeholder="Select priority"
                />
              </div>
              {/* Assign To (kept from previous popup behaviour) */}
              <MultiSelectLinkedField
                label="Assign To"
                value={taskForm.assignedTo}
                onChange={(vals) => setTaskForm((prev) => ({ ...prev, assignedTo: vals }))}
                options={(selectedChat?.members ?? []).map((m) => ({ value: m.id, label: m.name }))}
                placeholder="Search or select users..."
                onAddNew={() => { }}
                addNewLabel="Create New User"
              />
              {/* Reminder Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="task-reminder"
                  checked={taskForm.reminder}
                  onCheckedChange={(checked) =>
                    setTaskForm((prev) => ({ ...prev, reminder: checked as boolean }))
                  }
                />
                <Label htmlFor="task-reminder" className="text-sm font-medium cursor-pointer">
                  Reminder
                </Label>
              </div>
              {/* Reminder Date & Time (only shown if reminder is checked) */}
              {taskForm.reminder && (
                <StandardInputField
                  label="Reminder Date & Time"
                  value={taskForm.reminderDateTime}
                  onChange={(v) =>
                    setTaskForm((prev) => ({ ...prev, reminderDateTime: v as string }))
                  }
                  type="datetime"
                  placeholder="Select date and time"
                />
              )}
              {/* Action Section: Recurrence + Assign Task */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Actions</Label>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-light-bg dark:bg-dark-bg">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="flex flex-col items-center gap-1 p-2 rounded-md hover:bg-light-card-bg dark:hover:bg-dark-card-bg transition-colors"
                        >
                          <Repeat className="h-5 w-5 text-primary" />
                          <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                            Recurrence
                          </span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Set task to repeat on a schedule</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="flex flex-col items-center gap-1 p-2 rounded-md hover:bg-light-card-bg dark:hover:bg-dark-card-bg transition-colors"
                        >
                          <UserPlus className="h-5 w-5 text-primary" />
                          <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                            Assign Task
                          </span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Assign task to another person</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              {/* Task Completion */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Task Completion</Label>
                  <span className="text-sm font-medium text-primary">{taskForm.completion}%</span>
                </div>
                <Slider
                  value={[taskForm.completion]}
                  onValueChange={(value) =>
                    setTaskForm((prev) => ({ ...prev, completion: value[0] }))
                  }
                  max={100}
                  step={5}
                  className="w-full"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                className="bg-transparent"
                onClick={() => setShowTaskDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateTask}
                disabled={!taskForm.title.trim() || !taskForm.dueDate}
              >
                Save Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Event</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <StandardInputField
                label="Event Title"
                value={eventForm.title}
                onChange={(v) => setEventForm((prev) => ({ ...prev, title: v }))}
                placeholder="What's the event?"
                required
              />
              <StandardInputField
                label="Description"
                value={eventForm.description}
                onChange={(v) => setEventForm((prev) => ({ ...prev, description: v }))}
                placeholder="Add details..."
                type="textarea"
              />
              <StandardInputField
                label="Date"
                value={eventForm.date}
                onChange={(v) => setEventForm((prev) => ({ ...prev, date: v }))}
                type="date"
              />
              {!eventForm.isAllDay && (
                <StandardInputField
                  label="Time"
                  value={eventForm.time}
                  onChange={(v) => setEventForm((prev) => ({ ...prev, time: v }))}
                  type="time"
                />
              )}
              <div className="flex items-center gap-3">
                <Checkbox
                  id="event-is-all-day"
                  checked={eventForm.isAllDay}
                  onCheckedChange={(checked) => setEventForm((prev) => ({ ...prev, isAllDay: checked || false }))}
                />
                <Label htmlFor="event-is-all-day" className="cursor-pointer">All Day</Label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="event-is-recurring"
                  checked={eventForm.isRepeating}
                  onCheckedChange={(checked) => setEventForm((prev) => ({ ...prev, isRepeating: checked || false }))}
                />
                <Label htmlFor="event-is-recurring" className="cursor-pointer">Recurring</Label>
              </div>
              <StandardInputField
                label="Reminder"
                value={eventForm.reminder}
                onChange={(v) => setEventForm((prev) => ({ ...prev, reminder: v }))}
                type="datetime-local"
              />
              <Button variant="link">Open Full Form</Button>
              <div className="flex gap-2">
                <Button onClick={handleCreateEvent} className="flex-1">
                  Create Event
                </Button>
                <Button variant="outline" onClick={() => setShowEventDialog(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Group Settings Dialog — mirrors /communication/chat: group
            permissions (toggles) plus permission exemptions (specific
            users + user types). */}
        <Dialog open={showGroupSettingsDialog} onOpenChange={setShowGroupSettingsDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Group Settings - {selectedChat?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-4">Group Permissions</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Allow group members to initiate chat</Label>
                      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                        Members can start new conversations in this group
                      </p>
                    </div>
                    <Switch
                      checked={groupPermissions.allowInitiateChat}
                      onCheckedChange={(checked) =>
                        setGroupPermissions({ ...groupPermissions, allowInitiateChat: checked })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Allow group members to respond to chat</Label>
                      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                        Members can reply to messages in this group
                      </p>
                    </div>
                    <Switch
                      checked={groupPermissions.allowRespondToChat}
                      onCheckedChange={(checked) =>
                        setGroupPermissions({ ...groupPermissions, allowRespondToChat: checked })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Allow group members to edit chat</Label>
                      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                        Members can edit their own messages
                      </p>
                    </div>
                    <Switch
                      checked={groupPermissions.allowEditChat}
                      onCheckedChange={(checked) =>
                        setGroupPermissions({ ...groupPermissions, allowEditChat: checked })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Allow group members to delete sent chat</Label>
                      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                        Members can delete their own messages
                      </p>
                    </div>
                    <Switch
                      checked={groupPermissions.allowDeleteChat}
                      onCheckedChange={(checked) =>
                        setGroupPermissions({ ...groupPermissions, allowDeleteChat: checked })
                      }
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-medium mb-4">Permission Exemptions</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Exempted Users</Label>
                    <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-2">
                      These users are exempt from the above restrictions
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {groupPermissions.exemptedUsers.map((userId) => {
                        const user = selectedChat?.members?.find((m) => m.id === userId)
                        return user ? (
                          <Badge key={userId} variant="secondary">
                            {user.name}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="ml-1 h-4 w-4 p-0"
                              onClick={() =>
                                setGroupPermissions({
                                  ...groupPermissions,
                                  exemptedUsers: groupPermissions.exemptedUsers.filter(
                                    (id) => id !== userId,
                                  ),
                                })
                              }
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </Badge>
                        ) : null
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddExemptUserDialog(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add User
                    </Button>
                  </div>

                  <div>
                    <Label>Exempted User Types</Label>
                    <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-2">
                      These user types are exempt from the above restrictions
                    </p>
                    <div className="flex flex-wrap gap-4">
                      {["parent", "student", "staff", "supplier", "alumni"].map((type) => (
                        <div key={type} className="flex items-center space-x-2">
                          <Checkbox
                            id={`exempt-${type}`}
                            checked={groupPermissions.exemptedUserTypes.includes(type)}
                            onCheckedChange={(checked) => {
                              const current = groupPermissions.exemptedUserTypes
                              setGroupPermissions({
                                ...groupPermissions,
                                exemptedUserTypes: checked
                                  ? [...current, type]
                                  : current.filter((t) => t !== type),
                              })
                            }}
                          />
                          <Label
                            htmlFor={`exempt-${type}`}
                            className="capitalize text-sm cursor-pointer"
                          >
                            {type}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowGroupSettingsDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setShowGroupSettingsDialog(false)}>Save Settings</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Exempt User Dialog */}
        <Dialog open={showAddExemptUserDialog} onOpenChange={setShowAddExemptUserDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Exempted User</DialogTitle>
            </DialogHeader>
            <ScrollArea className="h-64">
              <div className="space-y-2">
                {(selectedChat?.members ?? [])
                  .filter((u) => !groupPermissions.exemptedUsers.includes(u.id))
                  .map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-3 p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg cursor-pointer"
                      onClick={() => {
                        setGroupPermissions({
                          ...groupPermissions,
                          exemptedUsers: [...groupPermissions.exemptedUsers, user.id],
                        })
                        setShowAddExemptUserDialog(false)
                      }}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.avatar || "/placeholder.svg"} />
                        <AvatarFallback className="text-xs">
                          {user.name.split(" ").map((n) => n[0]).join("")}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{user.name}</span>
                    </div>
                  ))}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Chat Settings Dialog — links to the same Chat Settings
            options used in /communication/chat. Available for both
            direct and group chats. */}
        <Dialog open={showChatSettingsDialog} onOpenChange={setShowChatSettingsDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Chat Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Mute Notifications</Label>
                  <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    Silence alerts from this chat
                  </p>
                </div>
                <Switch />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Read Receipts</Label>
                  <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    Show others when you have read their messages
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Typing Indicator</Label>
                  <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    Let others see when you are typing
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enter to Send</Label>
                  <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    Press Enter to send the message instead of a new line
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
              <Separator />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowChatSettingsDialog(false)}>
                  Close
                </Button>
                <Button onClick={() => setShowChatSettingsDialog(false)}>Save</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  const sourceDirectChats = beDirectChats ?? []
  const sourceGroupChats = beGroupChats ?? []

  const filteredDirectChats = sourceDirectChats.filter((chat) =>
    chat.name.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const filteredGroupChats = sourceGroupChats.filter((chat) => chat.name.toLowerCase().includes(searchTerm.toLowerCase()))

  // --- Chat List View ---
  return (
    <div className="w-96 h-[600px] bg-light-card-bg dark:bg-dark-card-bg rounded-lg shadow-xl flex flex-col border border-divider">
      <div className="p-4 border-b border-divider flex justify-between items-center cursor-grab">
        <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">Chat</h2>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close chat">
          <X className="h-5 w-5 text-light-text-primary dark:text-dark-text-primary" />
        </Button>
      </div>

      {/* Bulk Actions Bar for Chat Selection */}
      {isChatSelectionMode && selectedChats.length > 0 && (
        <div className="p-3 border-b border-divider bg-light-bg dark:bg-dark-bg flex items-center justify-between">
          <span className="text-sm text-light-text-primary dark:text-dark-text-primary">
            {selectedChats.length} selected
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => handleBulkChatAction("forward")}>
              <Forward className="h-4 w-4 mr-1" />
              Forward
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleBulkChatAction("delete")}>
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsChatSelectionMode(false)
                setSelectedChats([])
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="p-3 border-b border-divider">
        {/* Chat search only applies to Direct/Group lists — hidden on
            the Support tab, which is a single conversation. */}
        {activeTab !== "support" && (
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-light-text-secondary dark:text-dark-text-secondary" />
            <Input
              placeholder="Search chats..."
              className="pl-10 bg-light-bg dark:bg-dark-bg border-divider focus-visible:ring-primary"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-light-bg dark:bg-dark-bg">
            <TabsTrigger
              value="direct"
              className="data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              Direct
            </TabsTrigger>
            <TabsTrigger
              value="group"
              className="data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              Group
            </TabsTrigger>
            <TabsTrigger
              value="support"
              className="data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              Support
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <ScrollArea className="flex-1">
        {activeTab === "direct" && (
          <div className="p-3 space-y-1">
            {filteredDirectChats.length > 0 ? (
              filteredDirectChats.map(renderChatListItem)
            ) : (
              <div className="text-center py-10">
                <MessageCircle className="mx-auto h-12 w-12 text-[#16B1FF] mb-2" />
                <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  No direct chats found.
                </p>
              </div>
            )}
          </div>
        )}
        {activeTab === "group" && (
          <div className="p-3 space-y-1">
            {filteredGroupChats.length > 0 ? (
              filteredGroupChats.map(renderChatListItem)
            ) : (
              <div className="text-center py-10">
                <Users className="mx-auto h-12 w-12 text-[#16B1FF] mb-2" />
                <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">No group chats found.</p>
              </div>
            )}
          </div>
        )}
        {activeTab === "support" && (
          <div className="p-4 space-y-4">
            {/* Support agent header — always online. */}
            <div className="flex items-center gap-3 pb-3 mb-1 border-b border-divider">
              <div className="relative">
                <div className="w-9 h-9 bg-primary rounded-full flex items-center justify-center">
                  <LifeBuoy className="h-5 w-5 text-white" />
                </div>
                <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-success ring-2 ring-light-card-bg dark:ring-dark-card-bg" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary">
                  Support Chat
                </h3>
                <p className="text-xs text-success">Online • Typically replies in a few minutes</p>
              </div>
            </div>

            {/* Support conversation. */}
            {supportMessages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    message.sender === "user"
                      ? "bg-primary text-white"
                      : "bg-light-bg dark:bg-dark-bg text-light-text-primary dark:text-dark-text-primary"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                  <p
                    className={`text-xs mt-1 ${
                      message.sender === "user"
                        ? "text-white/70"
                        : "text-light-text-secondary dark:text-dark-text-secondary"
                    }`}
                  >
                    {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            ))}
            <div ref={supportMessagesEndRef} />
          </div>
        )}
      </ScrollArea>

      <div className="p-3 border-t border-divider">
        {activeTab === "direct" && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              setNewChatType("direct")
              setShowNewChatDialog(true)
            }}
          >
            <User className="mr-2 h-4 w-4" /> New Direct Chat
          </Button>
        )}
        {activeTab === "group" && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              setNewChatType("group")
              setShowNewChatDialog(true)
            }}
          >
            <Users className="mr-2 h-4 w-4" /> New Group Chat
          </Button>
        )}
        {/* Support tab — inline message composer instead of a
            "New Chat" button, since Support is a single conversation. */}
        {activeTab === "support" && (
          <div className="flex items-center gap-2">
            <Input
              type="text"
              placeholder="Type your message..."
              className="flex-1 bg-light-bg dark:bg-dark-bg border-divider focus-visible:ring-primary"
              value={supportInput}
              onChange={(e) => setSupportInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  handleSendSupportMessage()
                }
              }}
            />
            <Button
              size="icon"
              onClick={handleSendSupportMessage}
              disabled={!supportInput.trim()}
              className="bg-primary hover:bg-primary/90 flex-shrink-0"
              aria-label="Send message"
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
        )}
      </div>

      {/* New Chat Dialog */}
      <Dialog open={showNewChatDialog} onOpenChange={setShowNewChatDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{newChatType === "direct" ? "New Direct Chat" : "New Group Chat"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="chat-name">{newChatType === "direct" ? "Username or Email" : "Group Name"}</Label>
              <Input
                id="chat-name"
                value={newChatForm.name}
                onChange={(e) => setNewChatForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={newChatType === "direct" ? "Enter username or email" : "Enter group name"}
              />
            </div>
            {newChatType === "group" && (
              <div>
                <Label htmlFor="chat-description">Description (Optional)</Label>
                <Textarea
                  id="chat-description"
                  value={newChatForm.description}
                  onChange={(e) => setNewChatForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Group description"
                />
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={handleNewChat} className="flex-1">
                Create {newChatType === "direct" ? "Chat" : "Group"}
              </Button>
              <Button variant="outline" onClick={() => setShowNewChatDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}